import { useCallback, useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";

import {
  broilersByFarmUrl,
  cyclesByFacilityUrl,
  actTypesUrl,
  broilerDataByFarmUrl,
  broilerDataByFarmAndTypeUrl,
  convertStrainUrl,
  createFarmStrainUrl,
  farmStrainByFarmUrl,
  farmStrainUrl,
  farmsForUserUrl,
  practiceCreateUrl,
  practiceListUrl,
  removeBroilerDataUrl,
  removeCyclesUrl,
  removeFarmStrainUrl,
  strainDetailsUrl,
  updateBroilerDataUrl,
  updateCyclesUrl,
  updateFarmStrainUrl,
} from "api/endpoints";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { getStoredSession } from "utils/userIdentity";

const facilitySections = [
  {
    title: "Inputs",
    links: [
      { label: "DOCs", to: "/administration/facilities?section=docs" },
      { label: "Feed", to: "/administration/facilities?section=feed" },
      { label: "Health", to: "/administration/facilities?section=health" },
    ],
  },
  {
    title: "Management",
    links: [
      { label: "Production Cycle", to: "/administration/facilities?section=cycle" },
      { label: "Management Plans", to: "/administration/facilities?section=strain" },
      { label: "Production Systems", to: "/administration/facilities?section=systems" },
    ],
  },
];

const healthSections = ["Vaccination", "Sanitation", "Litter", "Health Program"];
const systemSections = ["Heating", "Ventilation", "Lighting", "Feed", "Drinker", "Controller"];
const systemTypeBySection = {
  Heating: 1,
  Ventilation: 3,
  Lighting: 4,
  Feed: 22,
  Drinker: 2,
};
const healthTypeBySection = {
  Vaccination: 15,
  Sanitation: 14,
  Litter: 12,
};
const activityTypeToBroilerDataType = {
  1: 15,
  2: 14,
  3: 12,
  4: 13,
};
const activityTypeToScheduleKey = {
  1: "vaccination_schedule",
  2: "water_sanitation_schedule",
  3: "litter_schedule",
  4: "house_sanitation_schedule",
};
const DROPDOWN_CACHE_KEY = "facilityLandingDropdownCache";

const readJsonStorage = (key) => {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn(`Unable to read ${key}`, error);
    return null;
  }
};

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && `${value}`.trim() !== "");

const getEntryFarmId = (entry) =>
  pickFirst(entry?.farmId, entry?.farmID, entry?.FarmID, entry?.raw?.farmId, entry?.raw?.farmID);

const getEntryFacilityId = (entry) =>
  pickFirst(
    entry?.broilerID,
    entry?.broilerId,
    entry?.BroilerID,
    entry?.facilityID,
    entry?.facilityId,
    entry?.id,
    entry?.ID
  );

const getEntryFacilityName = (entry) =>
  pickFirst(entry?.name, entry?.broilerName, entry?.facilityName, entry?.FacilityName);

const getCycleId = (entry) => pickFirst(entry?.id, entry?.ID, entry?.cycleId, entry?.cycleID);

const getCycleName = (entry) =>
  pickFirst(entry?.name, entry?.cycleName, entry?.CycleName, entry?.cycle, entry?.Cycle);

const getCycleFacilityId = (entry) =>
  pickFirst(
    entry?.broilerid,
    entry?.broilerId,
    entry?.BroilerID,
    entry?.facilityID,
    entry?.facilityId,
    entry?.raw?.broilerid,
    entry?.raw?.broilerId,
    entry?.raw?.BroilerID,
    entry?.raw?.facilityID,
    entry?.raw?.facilityId
  );

const getRecordId = (entry) => {
  if (Array.isArray(entry)) return getRecordId(entry[0]);
  if (typeof entry === "number" || typeof entry === "string") return entry;
  return pickFirst(entry?.id, entry?.ID, entry?.Id);
};

const getStrainRecordId = (entry) =>
  pickFirst(entry?.strain_id, entry?.strainId, entry?.strainID, entry?.StrainID);

const resolveUserId = () => {
  const storedSession = getStoredSession();
  const localSession = readJsonStorage("OmnioLogin");
  return pickFirst(
    storedSession?.ID,
    storedSession?.id,
    storedSession?.userID,
    storedSession?.userId,
    localSession?.ID,
    localSession?.id,
    localSession?.userID,
    localSession?.userId
  );
};

const resolveFarmId = () => {
  const session = getStoredSession();
  const sessionFarmId = getEntryFarmId(session);
  if (sessionFarmId != null) return sessionFarmId;

  const selectedFacility = readJsonStorage("facilityLandingSelections")?.facilityId;
  const broilerList = readJsonStorage("broilers");
  const cachedBroilers = readJsonStorage("OmnioBroilerLandingCache");
  const cacheData = Array.isArray(cachedBroilers?.data) ? cachedBroilers.data : cachedBroilers;
  const candidates = [broilerList, cacheData].filter(Array.isArray);

  if (selectedFacility != null) {
    const selectedMatch = candidates
      .flat()
      .find(
        (entry) => `${getEntryFacilityId(entry)}` === `${selectedFacility}` && getEntryFarmId(entry)
      );
    const selectedFarmId = getEntryFarmId(selectedMatch);
    if (selectedFarmId != null) return selectedFarmId;
  }

  const firstWithFarm = candidates.flat().find((entry) => getEntryFarmId(entry));
  return getEntryFarmId(firstWithFarm);
};

const mapProductionCycleRows = (facilities, cycles) => {
  const facilityLookup = (Array.isArray(facilities) ? facilities : []).reduce((acc, facility) => {
    const id = getEntryFacilityId(facility);
    const name = getEntryFacilityName(facility);
    if (id != null) {
      acc[String(id)] = {
        farmId: getEntryFarmId(facility),
        name: name ? String(name) : "",
      };
    }
    return acc;
  }, {});

  return (Array.isArray(cycles) ? cycles : [])
    .map((cycle, index) => {
      const id = getCycleId(cycle);
      const facilityId = getCycleFacilityId(cycle);
      const facility = facilityId != null ? facilityLookup[String(facilityId)] : null;
      const cycleName = getCycleName(cycle);
      const farmId = getEntryFarmId(cycle) ?? facility?.farmId;

      return {
        id: `production-cycle-${id ?? facilityId ?? index}`,
        sqlId: id,
        farmId,
        facilityId: facilityId != null ? String(facilityId) : "",
        facility: facility?.name ?? "",
        cycleName: cycleName ?? "",
      };
    })
    .filter((row) => row.facility || row.cycleName);
};

const mapProductionCycleFacilityOptions = (facilities) => {
  const seen = new Set();
  return (Array.isArray(facilities) ? facilities : []).reduce((options, facility) => {
    const facilityId = getEntryFacilityId(facility);
    const key = facilityId != null ? String(facilityId) : "";
    const farmId = getEntryFarmId(facility);
    const name = getEntryFacilityName(facility);
    if (!key || farmId == null || !name || seen.has(key)) return options;
    seen.add(key);
    return [...options, { id: key, name: String(name), farmId }];
  }, []);
};

const getObjectKeys = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? Object.keys(value) : [];

const getDaySortValue = (dayLabel) => {
  const match = String(dayLabel).match(/-?\d+/);
  return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
};

const sortDayOptions = (days) =>
  [...days].sort((left, right) => {
    const leftDay = getDaySortValue(left);
    const rightDay = getDaySortValue(right);
    if (leftDay !== rightDay) return leftDay - rightDay;
    return String(left).localeCompare(String(right), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });

const getStrainFile = (value) => {
  if (Array.isArray(value)) {
    const nestedFile = value.map(getStrainFile).find((file) => getObjectKeys(file).length > 0);
    return nestedFile ?? {};
  }

  if (!value || typeof value !== "object") return {};
  if (value.file && typeof value.file === "object" && !Array.isArray(value.file)) return value.file;
  if (value.data) return getStrainFile(value.data);
  if (value.result) return getStrainFile(value.result);
  if (value.strain) return getStrainFile(value.strain);
  return {};
};

const formatStrainValue = (value) => {
  if (Array.isArray(value)) return value.join(" - ");
  if (value === undefined || value === null) return "";
  return String(value);
};

const STRAIN_EQUALITY_OPTIONS = ["<", ">", ">=", "=", "interval", "record"];
const ENABLE_OPTIONS = ["true", "false"];

const getStrainEqualityOptions = (currentEquality) =>
  currentEquality && !STRAIN_EQUALITY_OPTIONS.includes(currentEquality)
    ? [...STRAIN_EQUALITY_OPTIONS, currentEquality]
    : STRAIN_EQUALITY_OPTIONS;

const parseStrainNumber = (value) => {
  if (value === "" || value === undefined || value === null) return null;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? value : numericValue;
};

const getPreviewStrainValue = (form) => {
  if (form.equality === "record") return form.enable === "true";
  if (form.equality === "<") return parseStrainNumber(form.highValue);
  if (form.equality === ">" || form.equality === ">=") return parseStrainNumber(form.lowValue);
  if (form.equality === "=" && form.lowValue === form.highValue) {
    return parseStrainNumber(form.lowValue);
  }
  return [parseStrainNumber(form.lowValue), parseStrainNumber(form.highValue)];
};

const getSelectedOptionByName = (options, name) => options.find((option) => option.name === name);

const getScheduledActivityConfirmationLabel = (category) =>
  String(category ?? "") === "Vaccinations" ? "Vaccination" : String(category ?? "");

const getSchedulePreviewValues = (form, activityTypeOptions = [], activityNameOptions = []) => {
  const selectedActivityType = getSelectedOptionByName(activityTypeOptions, form.activityCategory);
  const selectedActivityName = getSelectedOptionByName(activityNameOptions, form.activityName);
  const scheduleKey = activityTypeToScheduleKey[selectedActivityType?.id];

  if (!scheduleKey || !selectedActivityName?.id) return null;

  return {
    scheduleKey,
    schedule: {
      typeAct: {
        equality: "=",
        value: parseStrainNumber(selectedActivityType?.id),
      },
      nameId: {
        equality: "=",
        value: parseStrainNumber(selectedActivityName?.id),
      },
      dosage: {
        equality: "=",
        value: parseStrainNumber(form.dosage),
      },
      startDate: {
        equality: "=",
        value: form.startBy ?? "",
      },
      completedDateTime: {
        equality: "=",
        value: form.endBy ?? "",
      },
    },
  };
};

const isScheduleCategory = (category) => String(category).toLowerCase().includes("schedule");
const MORTALITY_TARGET_PARAMETER = "kpi_mortality_target";
const isMortalityTarget = (category, parameter) =>
  category === "mortality" && parameter === MORTALITY_TARGET_PARAMETER;

const getMortalityTargetDefaults = (record) => ({
  ...getStrainParameterDefaults(record),
  equality: "interval",
  enable: "",
});

const buildPreviewStrainFile = (
  strainFile,
  form,
  activityTypeOptions = [],
  activityNameOptions = []
) => {
  const previewFile = JSON.parse(JSON.stringify(strainFile ?? {}));
  if (!form.day) {
    return previewFile;
  }

  const mortalityTarget = isMortalityTarget(form.category, form.parametre);
  const hasMortalityRange =
    String(form.lowValue ?? "").trim() !== "" &&
    String(form.highValue ?? "").trim() !== "" &&
    Number.isFinite(Number(form.lowValue)) &&
    Number.isFinite(Number(form.highValue));
  if (
    form.category &&
    form.parametre &&
    previewFile?.[form.day]?.[form.category] &&
    (!mortalityTarget || hasMortalityRange)
  ) {
    const equality =
      mortalityTarget || (form.equality === "=" && form.lowValue !== form.highValue)
        ? "interval"
        : form.equality;
    previewFile[form.day][form.category][form.parametre] = {
      equality,
      value: getPreviewStrainValue({ ...form, equality }),
    };
  }

  const schedulePreview = getSchedulePreviewValues(form, activityTypeOptions, activityNameOptions);
  if (schedulePreview && previewFile?.[form.day]?.[schedulePreview.scheduleKey]) {
    previewFile[form.day][schedulePreview.scheduleKey] = schedulePreview.schedule;
  }

  return previewFile;
};

const addScheduledActivityToStrainFile = (
  strainFile,
  form,
  activityTypeOptions = [],
  activityNameOptions = []
) => {
  const schedulePreview = getSchedulePreviewValues(form, activityTypeOptions, activityNameOptions);
  if (!form.day || !schedulePreview) return strainFile;

  const nextFile = JSON.parse(JSON.stringify(strainFile ?? {}));
  nextFile[form.day] = nextFile[form.day] ?? {};
  nextFile[form.day][schedulePreview.scheduleKey] = schedulePreview.schedule;
  return nextFile;
};

const getStrainParameterDefaults = (parametreRecord) => {
  const equality =
    parametreRecord?.equality !== undefined && parametreRecord?.equality !== null
      ? String(parametreRecord.equality)
      : "";
  const value = parametreRecord?.value;

  if (Array.isArray(value)) {
    return {
      equality: equality || "interval",
      lowValue: formatStrainValue(value[0]),
      highValue: formatStrainValue(value[1]),
      enable: "",
    };
  }

  if (typeof value === "boolean") {
    return {
      equality: equality && STRAIN_EQUALITY_OPTIONS.includes(equality) ? equality : "record",
      lowValue: "",
      highValue: "",
      enable: String(value),
    };
  }

  if (equality === "<") {
    return { equality, lowValue: "", highValue: formatStrainValue(value), enable: "" };
  }

  if (equality === ">" || equality === ">=") {
    return { equality, lowValue: formatStrainValue(value), highValue: "", enable: "" };
  }

  if (equality === "=") {
    const formattedValue = formatStrainValue(value);
    return { equality, lowValue: formattedValue, highValue: formattedValue, enable: "" };
  }

  return {
    equality,
    lowValue: formatStrainValue(value),
    highValue: formatStrainValue(value),
    enable: "",
  };
};

const mapPreviewRows = (strainFile, form = {}) =>
  sortDayOptions(getObjectKeys(strainFile)).flatMap((day) => {
    const dayData = strainFile?.[day];
    return getObjectKeys(dayData)
      .filter((category) => !isScheduleCategory(category))
      .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }))
      .flatMap((category) => {
        const categoryData = dayData?.[category];
        return getObjectKeys(categoryData)
          .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }))
          .map((parametre) => {
            const defaults = getStrainParameterDefaults(categoryData?.[parametre]);
            return {
              id: `${day}-${category}-${parametre}`,
              day,
              parametre,
              equality: defaults.equality,
              lowValue: defaults.lowValue,
              highValue: defaults.highValue,
              enable: defaults.enable,
              startTime: form.startBy ?? "",
              endBy: form.endBy ?? "",
              dosage: form.dosage ? Number(form.dosage) : "",
            };
          });
      });
  });

const mapScheduledActivityPreviewRows = (strainFile) =>
  sortDayOptions(getObjectKeys(strainFile)).flatMap((day) => {
    const dayData = strainFile?.[day];
    return getObjectKeys(dayData)
      .filter(isScheduleCategory)
      .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }))
      .map((scheduleKey) => {
        const schedule = dayData?.[scheduleKey] ?? {};
        return {
          id: `${day}-${scheduleKey}`,
          day,
          parametre: scheduleKey,
          scheduleKey,
          startTime: schedule?.startDate?.value ?? "",
          endBy: schedule?.completedDateTime?.value ?? "",
          dosage: schedule?.dosage?.value ?? "",
        };
      });
  });

const getNextPracticeStrainId = (value) => {
  const rows = Array.isArray(value) ? value : Array.isArray(value?.data) ? value.data : [];
  const highestId = rows.reduce((highest, row) => {
    const strainId = Number(row?.strain_id);
    return Number.isNaN(strainId) ? highest : Math.max(highest, strainId);
  }, 0);
  return String(highestId + 1);
};

const getYamlContent = (value) => {
  if (typeof value === "string") return value;
  if (typeof value?.file === "string") return value.file;
  if (typeof value?.yaml === "string") return value.yaml;
  if (typeof value?.data === "string") return value.data;
  return JSON.stringify(value, null, 2);
};

const formatScheduledTime = (value) => (value ? `${value}:00+02:00` : "");

const getScheduledTimeInputValue = (value) => {
  const match = String(value ?? "").match(/^(\d{2}:\d{2})/);
  return match ? match[1] : "";
};

const isApiErrorResponse = (value) => {
  const status = Number(value?.status);
  return value?.error || (!Number.isNaN(status) && status >= 400);
};

function SectionTitle({ children }) {
  return (
    <MDBox mt={2} mb={1}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          bgcolor: "grey.200",
          px: 1,
          py: 0.5,
          fontSize: "0.95rem",
        }}
      >
        {children}
      </Typography>
    </MDBox>
  );
}

SectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
};

function ManagementPlanViewDialog({ plan, onClose }) {
  const [file, setFile] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [day, setDay] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        if (plan.strainId == null) throw new Error("Missing strain ID");
        const response = await axios.get(strainDetailsUrl(plan.strainId));
        if (isApiErrorResponse(response.data)) throw new Error("Unable to load plan");
        if (active) {
          const strainFile = getStrainFile(response.data);
          const firstDay = sortDayOptions(getObjectKeys(strainFile))[0] ?? "";
          setFile(strainFile);
          setDay(firstDay);
          setCategory(getObjectKeys(strainFile[firstDay]).sort()[0] ?? "");
        }
      } catch (loadError) {
        if (active) setError("Unable to load plan parameters. Please close and try again.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [plan.strainId]);

  const days = sortDayOptions(getObjectKeys(file));
  const selectedDays = day ? [day] : days;
  const categories = [...new Set(selectedDays.flatMap((key) => getObjectKeys(file[key])))].sort();
  const rows = selectedDays.flatMap((dayKey) =>
    getObjectKeys(file[dayKey])
      .filter((key) => !category || key === category)
      .flatMap((categoryKey) =>
        getObjectKeys(file[dayKey][categoryKey]).map((parameter) => {
          const record = file[dayKey][categoryKey][parameter];
          return {
            day: dayKey,
            category: categoryKey,
            parameter,
            ...getStrainParameterDefaults(record),
            equality: record?.equality ?? "",
          };
        })
      )
  );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1.5 }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox component="span">View Plan Parameters</MDBox>
          <IconButton aria-label="close view plan parameters" size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {plan.plan}
        </Typography>
        <Grid container spacing={2} pt={1}>
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              label="Day"
              value={day}
              sx={{
                "& .MuiOutlinedInput-root": { minHeight: 40 },
                "& .MuiSelect-select": {
                  minHeight: "1.4375em !important",
                  display: "flex",
                  alignItems: "center",
                  padding: "8.5px 12px !important",
                },
              }}
              disabled={loading || Boolean(error)}
              onChange={(event) => {
                setDay(event.target.value);
                setCategory("");
              }}
            >
              <MenuItem value="">All days</MenuItem>
              {days.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              label="Category"
              value={category}
              sx={{
                "& .MuiOutlinedInput-root": { minHeight: 40 },
                "& .MuiSelect-select": {
                  minHeight: "1.4375em !important",
                  display: "flex",
                  alignItems: "center",
                  padding: "8.5px 12px !important",
                },
              }}
              disabled={loading || Boolean(error)}
              onChange={(event) => setCategory(event.target.value)}
            >
              <MenuItem value="">All categories</MenuItem>
              {categories.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            {loading ? (
              <Typography variant="body2">Loading plan parameters...</Typography>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : rows.length === 0 ? (
              <Typography variant="body2">No plan parameters found.</Typography>
            ) : (
              rows.map((row) => (
                <Paper
                  key={JSON.stringify([row.day, row.category, row.parameter])}
                  variant="outlined"
                  sx={{ p: 2, mb: 2 }}
                >
                  <Grid container spacing={2}>
                    {[
                      ["parameter", "Parametre", 6],
                      ["equality", "Equality", 6],
                      ["lowValue", "Low Value", 4],
                      ["highValue", "High Value", 4],
                      ["enable", "Enable", 4],
                    ].map(([key, label, width]) => (
                      <Grid item xs={width} key={key}>
                        <TextField
                          fullWidth
                          label={label}
                          value={row[key]}
                          InputProps={{ readOnly: true }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              ))
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

ManagementPlanViewDialog.propTypes = {
  plan: PropTypes.shape({
    strainId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    plan: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

function AdministrationFacilities() {
  const [viewManagementPlan, setViewManagementPlan] = useState(null);
  const [docsDialogOpen, setDocsDialogOpen] = useState(false);
  const [productionCycleDialogOpen, setProductionCycleDialogOpen] = useState(false);
  const [productionCycleAddDialogOpen, setProductionCycleAddDialogOpen] = useState(false);
  const [productionCycleRows, setProductionCycleRows] = useState([]);
  const [productionCycleFacilityOptions, setProductionCycleFacilityOptions] = useState([]);
  const [productionCycleForm, setProductionCycleForm] = useState({ facilityId: "", cycleName: "" });
  const [productionCycleEditingId, setProductionCycleEditingId] = useState(null);
  const [productionCycleLoading, setProductionCycleLoading] = useState(false);
  const [productionCycleSaving, setProductionCycleSaving] = useState(false);
  const [productionCycleSort, setProductionCycleSort] = useState({
    orderBy: "facility",
    order: "asc",
  });
  const [managementPlansDialogOpen, setManagementPlansDialogOpen] = useState(false);
  const [managementPlanDialogOpen, setManagementPlanDialogOpen] = useState(false);
  const [managementPlanForm, setManagementPlanForm] = useState({
    farmId: "",
    plan: "",
    planOptionId: "",
  });
  const [managementPlanEditingId, setManagementPlanEditingId] = useState(null);
  const [managementPlanDialogMode, setManagementPlanDialogMode] = useState("edit");
  const [managementPlanSelectedIds, setManagementPlanSelectedIds] = useState([]);
  const [managementPlanAddOptions, setManagementPlanAddOptions] = useState([]);
  const [managementPlanAddLoading, setManagementPlanAddLoading] = useState(false);
  const [managementPlanRows, setManagementPlanRows] = useState([]);
  const [managementPlanFarmOptions, setManagementPlanFarmOptions] = useState([]);
  const [managementPlanPlanOptions, setManagementPlanPlanOptions] = useState([]);
  const [managementPlanDeleteMessage, setManagementPlanDeleteMessage] = useState("");
  const [managementPlanStrainDialogOpen, setManagementPlanStrainDialogOpen] = useState(false);
  const [managementPlanStrainLoading, setManagementPlanStrainLoading] = useState(false);
  const [managementPlanStrainSaving, setManagementPlanStrainSaving] = useState(false);
  const [managementPlanStrainOptions, setManagementPlanStrainOptions] = useState({
    days: [],
    categories: [],
    parametres: [],
    equalities: [],
    values: [],
  });
  const [managementPlanOriginalStrainFile, setManagementPlanOriginalStrainFile] = useState({});
  const [managementPlanStrainFile, setManagementPlanStrainFile] = useState({});
  const [managementPlanActivityTypeOptions, setManagementPlanActivityTypeOptions] = useState([]);
  const [managementPlanActivityNameOptions, setManagementPlanActivityNameOptions] = useState([]);
  const [managementPlanActivityNameLoading, setManagementPlanActivityNameLoading] = useState(false);
  const [managementPlanPreviewOpen, setManagementPlanPreviewOpen] = useState(false);
  const [managementPlanPreviewPage, setManagementPlanPreviewPage] = useState("facility");
  const [managementPlanPreviewFile, setManagementPlanPreviewFile] = useState({});
  const [managementPlanSaveConfirmation, setManagementPlanSaveConfirmation] = useState("");
  const [scheduledActivityConfirmation, setScheduledActivityConfirmation] = useState("");
  const [managementPlanStrainForm, setManagementPlanStrainForm] = useState({
    day: "",
    category: "",
    parametre: "",
    equality: "",
    lowValue: "",
    highValue: "",
    enable: "",
    planName: "",
    activityCategory: "",
    activityName: "",
    startBy: "",
    endBy: "",
    dosage: "",
  });
  const [managementPlanLoading, setManagementPlanLoading] = useState(false);
  const [managementPlanSaving, setManagementPlanSaving] = useState(false);
  const [managementPlanPendingDelete, setManagementPlanPendingDelete] = useState(null);
  const [managementPlanSort, setManagementPlanSort] = useState({
    orderBy: "farm",
    order: "asc",
  });
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [supplierForm, setSupplierForm] = useState({ supplierCode: "", supplierName: "" });
  const [supplierEditingId, setSupplierEditingId] = useState(null);
  const [supplierRows, setSupplierRows] = useState([]);
  const [supplierSort, setSupplierSort] = useState({ orderBy: "supplierCode", order: "asc" });
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [supplierSaving, setSupplierSaving] = useState(false);
  const [breederDialogOpen, setBreederDialogOpen] = useState(false);
  const [breederForm, setBreederForm] = useState({ breederCode: "", description: "" });
  const [breederEditingId, setBreederEditingId] = useState(null);
  const [breederRows, setBreederRows] = useState([]);
  const [breederSort, setBreederSort] = useState({ orderBy: "breederCode", order: "asc" });
  const [breederLoading, setBreederLoading] = useState(false);
  const [breederSaving, setBreederSaving] = useState(false);
  const [feedDialogOpen, setFeedDialogOpen] = useState(false);
  const [feedStageDialogOpen, setFeedStageDialogOpen] = useState(false);
  const [feedStageForm, setFeedStageForm] = useState({ stage: "" });
  const [feedStageEditingId, setFeedStageEditingId] = useState(null);
  const [feedStageRows, setFeedStageRows] = useState([]);
  const [feedStageSort, setFeedStageSort] = useState({ orderBy: "stage", order: "asc" });
  const [feedStageLoading, setFeedStageLoading] = useState(false);
  const [feedStageSaving, setFeedStageSaving] = useState(false);
  const [feedNameDialogOpen, setFeedNameDialogOpen] = useState(false);
  const [feedNameForm, setFeedNameForm] = useState({ name: "", description: "" });
  const [feedNameEditingId, setFeedNameEditingId] = useState(null);
  const [feedNameRows, setFeedNameRows] = useState([]);
  const [feedNameSort, setFeedNameSort] = useState({ orderBy: "name", order: "asc" });
  const [feedNameLoading, setFeedNameLoading] = useState(false);
  const [feedNameSaving, setFeedNameSaving] = useState(false);
  const [feedSupplierDialogOpen, setFeedSupplierDialogOpen] = useState(false);
  const [feedSupplierForm, setFeedSupplierForm] = useState({ name: "", description: "" });
  const [feedSupplierEditingId, setFeedSupplierEditingId] = useState(null);
  const [feedSupplierRows, setFeedSupplierRows] = useState([]);
  const [feedSupplierSort, setFeedSupplierSort] = useState({ orderBy: "name", order: "asc" });
  const [feedSupplierLoading, setFeedSupplierLoading] = useState(false);
  const [feedSupplierSaving, setFeedSupplierSaving] = useState(false);
  const [healthDialogOpen, setHealthDialogOpen] = useState(false);
  const [healthItemDialogOpen, setHealthItemDialogOpen] = useState(false);
  const [healthItemSection, setHealthItemSection] = useState("");
  const [healthItemForm, setHealthItemForm] = useState({ name: "", description: "" });
  const [healthItemEditingId, setHealthItemEditingId] = useState(null);
  const [healthRows, setHealthRows] = useState(() =>
    healthSections.reduce((acc, section) => ({ ...acc, [section]: [] }), {})
  );
  const [healthLoading, setHealthLoading] = useState(() =>
    healthSections.reduce((acc, section) => ({ ...acc, [section]: false }), {})
  );
  const [healthSaving, setHealthSaving] = useState(false);
  const [healthSort, setHealthSort] = useState(() =>
    healthSections.reduce(
      (acc, section) => ({ ...acc, [section]: { orderBy: "name", order: "asc" } }),
      {}
    )
  );
  const [systemsDialogOpen, setSystemsDialogOpen] = useState(false);
  const [systemItemDialogOpen, setSystemItemDialogOpen] = useState(false);
  const [systemItemSection, setSystemItemSection] = useState("");
  const [systemItemForm, setSystemItemForm] = useState({ makeModel: "", description: "" });
  const [systemItemEditingId, setSystemItemEditingId] = useState(null);
  const [systemRows, setSystemRows] = useState(() =>
    systemSections.reduce((acc, section) => ({ ...acc, [section]: [] }), {})
  );
  const [systemLoading, setSystemLoading] = useState(() =>
    systemSections.reduce((acc, section) => ({ ...acc, [section]: false }), {})
  );
  const [systemSaving, setSystemSaving] = useState(false);
  const [systemSort, setSystemSort] = useState(() =>
    systemSections.reduce(
      (acc, section) => ({ ...acc, [section]: { orderBy: "makeModel", order: "asc" } }),
      {}
    )
  );

  const loadProductionCycleRows = useCallback(async () => {
    const cachedDropdowns = readJsonStorage(DROPDOWN_CACHE_KEY);
    if (cachedDropdowns) {
      setProductionCycleRows(
        mapProductionCycleRows(cachedDropdowns.facilities, cachedDropdowns.cycles)
      );
      setProductionCycleFacilityOptions(
        mapProductionCycleFacilityOptions(cachedDropdowns.facilities)
      );
    }

    const userID = resolveUserId();
    if (!userID) {
      if (!cachedDropdowns) {
        setProductionCycleRows([]);
        setProductionCycleFacilityOptions([]);
      }
      return;
    }

    setProductionCycleLoading(true);
    try {
      const farmsRes = await axios.get(farmsForUserUrl(userID));
      const farmsData = Array.isArray(farmsRes.data) ? farmsRes.data : [];
      const farmIds = [
        ...new Set(
          farmsData
            .map((farm) => pickFirst(farm?.id, farm?.ID, farm?.farmId, farm?.farmID, farm?.FarmID))
            .filter((id) => id != null)
            .map(String)
        ),
      ];
      const facilitiesByFarm = await Promise.all(
        farmIds.map(async (farmId) => {
          const response = await axios.get(broilersByFarmUrl(farmId));
          return (Array.isArray(response.data) ? response.data : [])
            .map((facility) => ({
              id: pickFirst(facility?.id, facility?.ID, getEntryFacilityId(facility)),
              name: getEntryFacilityName(facility),
              farmId,
            }))
            .filter((facility) => facility.id != null && facility.name);
        })
      );
      const facilities = facilitiesByFarm.flat();
      const cyclesByFacility = await Promise.all(
        facilities.map(async (facility) => {
          const response = await axios.get(cyclesByFacilityUrl(facility.id));
          return (Array.isArray(response.data) ? response.data : []).map((cycle) => ({
            ...cycle,
            broilerid: facility.id,
            farmId: facility.farmId,
          }));
        })
      );
      const cycles = cyclesByFacility.flat();

      setProductionCycleRows(mapProductionCycleRows(facilities, cycles));
      setProductionCycleFacilityOptions(mapProductionCycleFacilityOptions(facilities));
    } catch (error) {
      console.error("Failed to load production cycles", error);
      if (!cachedDropdowns) {
        setProductionCycleRows([]);
        setProductionCycleFacilityOptions([]);
      }
    } finally {
      setProductionCycleLoading(false);
    }
  }, []);

  useEffect(() => {
    if (productionCycleDialogOpen) {
      loadProductionCycleRows();
    }
  }, [productionCycleDialogOpen, loadProductionCycleRows]);

  const loadManagementPlanRows = useCallback(async () => {
    const userID = resolveUserId();
    if (!userID) {
      setManagementPlanRows([]);
      setManagementPlanFarmOptions([]);
      setManagementPlanPlanOptions([]);
      return;
    }

    setManagementPlanLoading(true);
    try {
      const [farmsResponse, strainResponse] = await Promise.all([
        axios.get(farmsForUserUrl(userID)),
        axios.get(farmStrainUrl()),
      ]);
      const farms = Array.isArray(farmsResponse.data) ? farmsResponse.data : [];
      const farmOptions = farms
        .filter((farm) => {
          const farmUserId = pickFirst(farm?.userID, farm?.userId, farm?.UserID);
          return farmUserId == null || String(farmUserId) === String(userID);
        })
        .map((farm) => ({
          id: pickFirst(farm?.farmId, farm?.farmID, farm?.FarmID, farm?.id, farm?.ID),
          name: pickFirst(farm?.name, farm?.farmName, farm?.FarmName),
        }))
        .filter((farm) => farm.id != null && farm.name);

      setManagementPlanFarmOptions(farmOptions);

      const farmNameById = farmOptions.reduce((acc, farm) => {
        acc[String(farm.id)] = farm.name;
        return acc;
      }, {});
      const farmIds = new Set(farmOptions.map((farm) => String(farm.id)));
      const strains = Array.isArray(strainResponse.data) ? strainResponse.data : [];
      const strainOptions = strains
        .map((row, index) => {
          const farmId = pickFirst(row?.farmId, row?.farmID, row?.FarmID);
          const issuer = pickFirst(row?.issuer, row?.Issuer);
          const name = pickFirst(row?.name, row?.Name);
          const sqlId = getRecordId(row);
          const strainId = pickFirst(
            row?.strain_id,
            row?.strainId,
            row?.strainID,
            row?.StrainID,
            row?.strain?.id,
            row?.strain?._id,
            row?._id,
            sqlId
          );
          return {
            id: `management-plan-strain-${sqlId ?? farmId ?? index}`,
            sqlId,
            strainId,
            farmId,
            issuer,
            farm: farmId != null ? farmNameById[String(farmId)] ?? "" : "",
            plan: name ?? "",
          };
        })
        .filter(
          (row) =>
            row.farmId != null &&
            (String(row.farmId) === "0" || farmIds.has(String(row.farmId))) &&
            row.plan
        );

      setManagementPlanPlanOptions(strainOptions);
      setManagementPlanRows(strainOptions);
    } catch (error) {
      console.error("Failed to load management plans", error);
      setManagementPlanRows([]);
      setManagementPlanPlanOptions([]);
    } finally {
      setManagementPlanLoading(false);
    }
  }, []);

  useEffect(() => {
    if (managementPlansDialogOpen) {
      loadManagementPlanRows();
    }
  }, [managementPlansDialogOpen, loadManagementPlanRows]);

  const loadManagementPlanAddOptions = useCallback(async () => {
    const farmId =
      managementPlanFarmOptions[0]?.id ??
      managementPlanRows.find((row) => String(row.farmId) !== "0")?.farmId ??
      resolveFarmId();
    if (farmId == null) {
      setManagementPlanAddOptions([]);
      return;
    }

    setManagementPlanAddLoading(true);
    try {
      const availableResponse = await axios.post(
        createFarmStrainUrl(),
        { farmId: Number(farmId) },
        { headers: { "Content-Type": "application/json" } }
      );
      const existingResponse = await axios.get(farmStrainByFarmUrl(farmId));
      const existingRows = Array.isArray(existingResponse.data)
        ? existingResponse.data
        : managementPlanRows.filter((row) => String(row.farmId) === String(farmId));
      const existingStrainIds = new Set(
        existingRows
          .map((row) => pickFirst(row?.strainId, row?.strainID, row?.StrainID))
          .filter((strainId) => strainId != null)
          .map((strainId) => String(strainId))
      );
      const availableRows = Array.isArray(availableResponse.data)
        ? availableResponse.data
        : Array.isArray(availableResponse.data?.data)
        ? availableResponse.data.data
        : [];
      const addOptions = availableRows
        .map((row, index) => {
          const strainId = getStrainRecordId(row);
          const name = pickFirst(row?.value?.name, row?.value?.Name, row?.name, row?.Name);
          const ownerId = pickFirst(row?.value?.ownerId, row?.value?.ownerID, row?.ownerId);
          const issuer = pickFirst(row?.value?.issuer, row?.value?.Issuer, row?.issuer);
          return {
            id: `management-plan-add-${strainId ?? index}`,
            strainId,
            ownerId,
            issuer,
            plan: name ?? "",
          };
        })
        .filter(
          (option) =>
            option.strainId != null &&
            option.plan &&
            !existingStrainIds.has(String(option.strainId))
        );

      setManagementPlanAddOptions(addOptions);
    } catch (error) {
      console.error("Failed to load add management plans", error);
      setManagementPlanAddOptions([]);
    } finally {
      setManagementPlanAddLoading(false);
    }
  }, [managementPlanFarmOptions, managementPlanRows]);

  const loadSupplierRows = useCallback(async () => {
    const farmId = resolveFarmId();

    if (farmId == null) {
      console.error("Unable to load suppliers: farmId was not found in login or broiler cache.");
      setSupplierRows([]);
      return;
    }

    setSupplierLoading(true);
    try {
      const response = await axios.get(broilerDataByFarmUrl(farmId));
      const rows = Array.isArray(response.data) ? response.data : [];
      setSupplierRows(
        rows
          .filter((row) => Number(row?.type) === 8)
          .map((row, index) => {
            const sqlId = getRecordId(row);
            return {
              id: `supplier-${sqlId ?? index}`,
              sqlId,
              supplierCode: row?.value ?? "",
              supplierName: row?.description ?? "",
            };
          })
      );
    } catch (error) {
      console.error("Failed to load suppliers", error);
    } finally {
      setSupplierLoading(false);
    }
  }, []);

  useEffect(() => {
    if (docsDialogOpen) {
      loadSupplierRows();
    }
  }, [docsDialogOpen, loadSupplierRows]);

  const loadBreederRows = useCallback(async () => {
    const farmId = resolveFarmId();

    if (farmId == null) {
      console.error("Unable to load breeders: farmId was not found in login or broiler cache.");
      setBreederRows([]);
      return;
    }

    setBreederLoading(true);
    try {
      const response = await axios.get(broilerDataByFarmUrl(farmId));
      const rows = Array.isArray(response.data) ? response.data : [];
      setBreederRows(
        rows
          .filter((row) => Number(row?.type) === 9)
          .map((row, index) => {
            const sqlId = getRecordId(row);
            return {
              id: `breeder-${sqlId ?? index}`,
              sqlId,
              breederCode: row?.value ?? "",
              description: row?.description ?? "",
            };
          })
      );
    } catch (error) {
      console.error("Failed to load breeders", error);
    } finally {
      setBreederLoading(false);
    }
  }, []);

  useEffect(() => {
    if (docsDialogOpen) {
      loadBreederRows();
    }
  }, [docsDialogOpen, loadBreederRows]);

  const loadFeedStageRows = useCallback(async () => {
    const farmId = resolveFarmId();

    if (farmId == null) {
      console.error("Unable to load feed stages: farmId was not found in login or broiler cache.");
      setFeedStageRows([]);
      return;
    }

    setFeedStageLoading(true);
    try {
      const response = await axios.get(broilerDataByFarmUrl(farmId));
      const rows = Array.isArray(response.data) ? response.data : [];
      setFeedStageRows(
        rows
          .filter((row) => Number(row?.type) === 10)
          .map((row, index) => {
            const sqlId = getRecordId(row);
            return {
              id: `feed-stage-${sqlId ?? index}`,
              sqlId,
              stage: row?.value ?? "",
            };
          })
      );
    } catch (error) {
      console.error("Failed to load feed stages", error);
    } finally {
      setFeedStageLoading(false);
    }
  }, []);

  useEffect(() => {
    if (feedDialogOpen) {
      loadFeedStageRows();
    }
  }, [feedDialogOpen, loadFeedStageRows]);

  const loadFeedNameRows = useCallback(async () => {
    const farmId = resolveFarmId();

    if (farmId == null) {
      console.error("Unable to load feed names: farmId was not found in login or broiler cache.");
      setFeedNameRows([]);
      return;
    }

    setFeedNameLoading(true);
    try {
      const response = await axios.get(broilerDataByFarmUrl(farmId));
      const rows = Array.isArray(response.data) ? response.data : [];
      setFeedNameRows(
        rows
          .filter((row) => Number(row?.type) === 5)
          .map((row, index) => {
            const sqlId = getRecordId(row);
            return {
              id: `feed-name-${sqlId ?? index}`,
              sqlId,
              name: row?.value ?? "",
              description: row?.description ?? "",
            };
          })
      );
    } catch (error) {
      console.error("Failed to load feed names", error);
    } finally {
      setFeedNameLoading(false);
    }
  }, []);

  useEffect(() => {
    if (feedDialogOpen) {
      loadFeedNameRows();
    }
  }, [feedDialogOpen, loadFeedNameRows]);

  const loadFeedSupplierRows = useCallback(async () => {
    const farmId = resolveFarmId();

    if (farmId == null) {
      console.error(
        "Unable to load feed suppliers: farmId was not found in login or broiler cache."
      );
      setFeedSupplierRows([]);
      return;
    }

    setFeedSupplierLoading(true);
    try {
      const response = await axios.get(broilerDataByFarmUrl(farmId));
      const rows = Array.isArray(response.data) ? response.data : [];
      setFeedSupplierRows(
        rows
          .filter((row) => Number(row?.type) === 11)
          .map((row, index) => {
            const sqlId = getRecordId(row);
            return {
              id: `feed-supplier-${sqlId ?? index}`,
              sqlId,
              name: row?.value ?? "",
              description: row?.description ?? "",
            };
          })
      );
    } catch (error) {
      console.error("Failed to load feed suppliers", error);
    } finally {
      setFeedSupplierLoading(false);
    }
  }, []);

  useEffect(() => {
    if (feedDialogOpen) {
      loadFeedSupplierRows();
    }
  }, [feedDialogOpen, loadFeedSupplierRows]);

  const loadHealthRows = useCallback(async (section) => {
    const type = healthTypeBySection[section];
    if (type == null) return;

    const farmId = resolveFarmId();

    if (farmId == null) {
      console.error(`Unable to load ${section}: farmId was not found in login or broiler cache.`);
      setHealthRows((rows) => ({ ...rows, [section]: [] }));
      return;
    }

    setHealthLoading((loading) => ({ ...loading, [section]: true }));
    try {
      const response = await axios.get(broilerDataByFarmUrl(farmId));
      const rows = Array.isArray(response.data) ? response.data : [];
      setHealthRows((currentRows) => ({
        ...currentRows,
        [section]: rows
          .filter((row) => Number(row?.type) === type)
          .map((row, index) => {
            const sqlId = getRecordId(row);
            return {
              id: `health-${section}-${sqlId ?? index}`,
              sqlId,
              name: row?.value ?? "",
              description: row?.description ?? "",
            };
          }),
      }));
    } catch (error) {
      console.error(`Failed to load ${section}`, error);
    } finally {
      setHealthLoading((loading) => ({ ...loading, [section]: false }));
    }
  }, []);

  useEffect(() => {
    if (healthDialogOpen) {
      Object.keys(healthTypeBySection).forEach((section) => {
        loadHealthRows(section);
      });
    }
  }, [healthDialogOpen, loadHealthRows]);

  const handleOpenSupplierDialog = () => {
    setSupplierForm({ supplierCode: "", supplierName: "" });
    setSupplierEditingId(null);
    setSupplierDialogOpen(true);
  };

  const handleOpenSupplierEditDialog = (row) => {
    setSupplierForm({
      supplierCode: row.supplierCode ?? "",
      supplierName: row.supplierName ?? "",
    });
    setSupplierEditingId(row.id);
    setSupplierDialogOpen(true);
  };

  const handleCloseSupplierDialog = () => {
    setSupplierDialogOpen(false);
    setSupplierEditingId(null);
    setSupplierForm({ supplierCode: "", supplierName: "" });
  };

  const handleSaveSupplierRow = async () => {
    const farmId = resolveFarmId();

    if (farmId == null) {
      console.error("Unable to save supplier: farmId was not found in login or broiler cache.");
      return;
    }

    const payload = {
      farmId,
      type: 8,
      value: supplierForm.supplierCode,
      description: supplierForm.supplierName,
    };
    const editingRow = supplierRows.find((row) => row.id === supplierEditingId);
    if (supplierEditingId != null && editingRow?.sqlId == null) {
      console.error("Unable to edit supplier: SQL row id is missing.");
      return;
    }
    if (editingRow?.sqlId != null) {
      payload.id = editingRow.sqlId;
    }

    setSupplierSaving(true);
    try {
      const response = await axios.post(updateBroilerDataUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
      if (supplierEditingId == null) {
        const responseId = getRecordId(response?.data);
        setSupplierRows((rows) => [
          ...rows,
          {
            id: `supplier-${Date.now()}-${rows.length}`,
            sqlId: responseId,
            supplierCode: supplierForm.supplierCode,
            supplierName: supplierForm.supplierName,
          },
        ]);
      }
      await loadSupplierRows();
      handleCloseSupplierDialog();
    } catch (error) {
      console.error("Failed to save supplier", error);
    } finally {
      setSupplierSaving(false);
    }
  };

  const handleDeleteSupplierRow = async (row) => {
    if (row?.sqlId == null) {
      console.error("Unable to delete supplier: SQL row id is missing.");
      return;
    }

    try {
      await axios.post(
        removeBroilerDataUrl(),
        { id: row.sqlId },
        { headers: { "Content-Type": "application/json" } }
      );
      await loadSupplierRows();
    } catch (error) {
      console.error("Failed to delete supplier", error);
    }
  };

  const handleOpenBreederDialog = () => {
    setBreederForm({ breederCode: "", description: "" });
    setBreederEditingId(null);
    setBreederDialogOpen(true);
  };

  const handleOpenBreederEditDialog = (row) => {
    setBreederForm({
      breederCode: row.breederCode ?? "",
      description: row.description ?? "",
    });
    setBreederEditingId(row.id);
    setBreederDialogOpen(true);
  };

  const handleCloseBreederDialog = () => {
    setBreederDialogOpen(false);
    setBreederEditingId(null);
    setBreederForm({ breederCode: "", description: "" });
  };

  const handleSaveBreederRow = async () => {
    const farmId = resolveFarmId();

    if (farmId == null) {
      console.error("Unable to save breeder: farmId was not found in login or broiler cache.");
      return;
    }

    const payload = {
      farmId,
      type: 9,
      value: breederForm.breederCode,
      description: breederForm.description,
    };
    const editingRow = breederRows.find((row) => row.id === breederEditingId);
    if (breederEditingId != null && editingRow?.sqlId == null) {
      console.error("Unable to edit breeder: SQL row id is missing.");
      return;
    }
    if (editingRow?.sqlId != null) {
      payload.id = editingRow.sqlId;
    }

    setBreederSaving(true);
    try {
      const response = await axios.post(updateBroilerDataUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
      if (breederEditingId == null) {
        const responseId = getRecordId(response?.data);
        setBreederRows((rows) => [
          ...rows,
          {
            id: `breeder-${Date.now()}-${rows.length}`,
            sqlId: responseId,
            breederCode: breederForm.breederCode,
            description: breederForm.description,
          },
        ]);
      }
      await loadBreederRows();
      handleCloseBreederDialog();
    } catch (error) {
      console.error("Failed to save breeder", error);
    } finally {
      setBreederSaving(false);
    }
  };

  const handleDeleteBreederRow = async (row) => {
    if (row?.sqlId == null) {
      console.error("Unable to delete breeder: SQL row id is missing.");
      return;
    }

    try {
      await axios.post(
        removeBroilerDataUrl(),
        { id: row.sqlId },
        { headers: { "Content-Type": "application/json" } }
      );
      await loadBreederRows();
    } catch (error) {
      console.error("Failed to delete breeder", error);
    }
  };

  const handleProductionCycleSort = (orderBy) => {
    setProductionCycleSort((sort) => ({
      orderBy,
      order: sort.orderBy === orderBy && sort.order === "asc" ? "desc" : "asc",
    }));
  };

  const handleOpenProductionCycleAddDialog = () => {
    setProductionCycleForm({ facilityId: "", cycleName: "" });
    setProductionCycleEditingId(null);
    setProductionCycleAddDialogOpen(true);
  };

  const handleOpenProductionCycleEditDialog = (row) => {
    setProductionCycleForm({
      facilityId: row.facilityId ?? "",
      cycleName: row.cycleName ?? "",
    });
    setProductionCycleEditingId(row.sqlId ?? null);
    setProductionCycleAddDialogOpen(true);
  };

  const handleCloseProductionCycleAddDialog = () => {
    setProductionCycleAddDialogOpen(false);
    setProductionCycleForm({ facilityId: "", cycleName: "" });
    setProductionCycleEditingId(null);
  };

  const handleAddProductionCycleRow = async () => {
    const selectedFacility = productionCycleFacilityOptions.find(
      (option) => option.id === productionCycleForm.facilityId
    );

    if (!selectedFacility || !productionCycleForm.cycleName.trim()) return;

    const payload = {
      broilerid: Number(selectedFacility.id),
      name: productionCycleForm.cycleName.trim(),
      farmId: Number(selectedFacility.farmId),
    };
    if (productionCycleEditingId != null) {
      payload.id = Number(productionCycleEditingId);
    }

    setProductionCycleSaving(true);
    try {
      await axios.post(updateCyclesUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
      handleCloseProductionCycleAddDialog();
      await loadProductionCycleRows();
    } catch (error) {
      console.error("Failed to save production cycle", error);
    } finally {
      setProductionCycleSaving(false);
    }
  };

  const handleDeleteProductionCycleRow = async (row) => {
    if (row?.sqlId == null) {
      console.error("Unable to delete production cycle: SQL row id is missing.");
      return;
    }

    const payload = {
      id: Number(row.sqlId),
      broilerid: Number(row.facilityId),
      name: row.cycleName,
      farmId: Number(row.farmId),
    };

    setProductionCycleSaving(true);
    try {
      await axios.post(removeCyclesUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
      await loadProductionCycleRows();
    } catch (error) {
      console.error("Failed to delete production cycle", error);
    } finally {
      setProductionCycleSaving(false);
    }
  };

  const sortedProductionCycleRows = [...productionCycleRows].sort((a, b) => {
    const left = String(a[productionCycleSort.orderBy] ?? "");
    const right = String(b[productionCycleSort.orderBy] ?? "");
    const result = left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
    return productionCycleSort.order === "asc" ? result : -result;
  });

  const handleManagementPlanSort = (orderBy) => {
    setManagementPlanSort((sort) => ({
      orderBy,
      order: sort.orderBy === orderBy && sort.order === "asc" ? "desc" : "asc",
    }));
  };

  const sortedManagementPlanRows = [...managementPlanRows].sort((a, b) => {
    const left = String(a[managementPlanSort.orderBy] ?? "");
    const right = String(b[managementPlanSort.orderBy] ?? "");
    const result = left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
    return managementPlanSort.order === "asc" ? result : -result;
  });
  const handleOpenManagementPlanDialog = () => {
    setManagementPlanForm({ farmId: "", plan: "", planOptionId: "" });
    setManagementPlanEditingId(null);
    setManagementPlanDialogMode("add");
    setManagementPlanSelectedIds([]);
    setManagementPlanDialogOpen(true);
    loadManagementPlanAddOptions();
  };

  const handleOpenManagementPlanEditDialog = (row) => {
    const selectedOption =
      managementPlanPlanOptions.find((option) => option.id === row.id) ??
      managementPlanPlanOptions.find(
        (option) =>
          String(option.sqlId ?? "") === String(row.sqlId ?? "") &&
          String(option.farmId ?? "") === String(row.farmId ?? "")
      ) ??
      managementPlanPlanOptions.find(
        (option) =>
          String(option.farmId ?? "") === String(row.farmId ?? "") && option.plan === row.plan
      );
    setManagementPlanForm({
      farmId: selectedOption?.farmId != null ? String(selectedOption.farmId) : "",
      plan: selectedOption?.plan ?? "",
      planOptionId: selectedOption?.id ?? "",
    });
    setManagementPlanEditingId(row.id);
    setManagementPlanDialogMode("edit");
    setManagementPlanSelectedIds([]);
    setManagementPlanDialogOpen(true);
  };

  const handleCloseManagementPlanStrainDialog = () => {
    setManagementPlanStrainDialogOpen(false);
    setManagementPlanStrainForm({
      day: "",
      category: "",
      parametre: "",
      equality: "",
      lowValue: "",
      highValue: "",
      enable: "",
      planName: "",
      activityCategory: "",
      activityName: "",
      startBy: "",
      endBy: "",
      dosage: "",
    });
    setManagementPlanStrainOptions({
      days: [],
      categories: [],
      parametres: [],
      equalities: [],
      values: [],
    });
    setManagementPlanOriginalStrainFile({});
    setManagementPlanStrainFile({});
    setManagementPlanActivityTypeOptions([]);
    setManagementPlanActivityNameOptions([]);
    setManagementPlanActivityNameLoading(false);
    setManagementPlanPreviewOpen(false);
    setManagementPlanPreviewPage("facility");
    setManagementPlanPreviewFile({});
    setManagementPlanStrainSaving(false);
  };

  const handlePreviewManagementPlanStrain = () => {
    setManagementPlanPreviewFile(
      buildPreviewStrainFile(
        managementPlanStrainFile,
        managementPlanStrainForm,
        managementPlanActivityTypeOptions,
        managementPlanActivityNameOptions
      )
    );
    setManagementPlanPreviewPage("facility");
    setManagementPlanPreviewOpen(true);
  };

  const handleAddScheduledActivity = () => {
    setManagementPlanStrainFile((strainFile) =>
      addScheduledActivityToStrainFile(
        strainFile,
        managementPlanStrainForm,
        managementPlanActivityTypeOptions,
        managementPlanActivityNameOptions
      )
    );
    setScheduledActivityConfirmation(
      `${getScheduledActivityConfirmationLabel(
        managementPlanStrainForm.activityCategory
      )} has successfully been added`
    );
  };

  const handleDeleteScheduledActivityPreviewRow = (row) => {
    setManagementPlanStrainFile((strainFile) => {
      const nextFile = JSON.parse(JSON.stringify(strainFile ?? {}));
      if (nextFile?.[row.day]?.[row.scheduleKey]) {
        delete nextFile[row.day][row.scheduleKey];
      }
      return nextFile;
    });
    setManagementPlanPreviewFile((strainFile) => {
      const nextFile = JSON.parse(JSON.stringify(strainFile ?? {}));
      if (nextFile?.[row.day]?.[row.scheduleKey]) {
        delete nextFile[row.day][row.scheduleKey];
      }
      return nextFile;
    });
  };

  const handleSaveManagementPlanStrain = async () => {
    if (!managementPlanStrainForm.planName.trim()) return;

    setManagementPlanStrainSaving(true);
    try {
      const planName = managementPlanStrainForm.planName.trim();
      const draftFile = buildPreviewStrainFile(
        managementPlanStrainFile,
        managementPlanStrainForm,
        managementPlanActivityTypeOptions,
        managementPlanActivityNameOptions
      );
      const practiceListResponse = await axios.post(practiceListUrl());
      const nextStrainId = getNextPracticeStrainId(practiceListResponse.data);
      const convertResponse = await axios.post(convertStrainUrl(), draftFile, {
        headers: { "Content-Type": "application/json" },
      });
      const yamlContent = getYamlContent(convertResponse.data);
      const yamlFile = new Blob([yamlContent], { type: "text/yaml" });
      const formData = new FormData();
      formData.append("file", yamlFile, "strain.yml");
      formData.append("file_id", nextStrainId);

      await axios.post(practiceCreateUrl(), formData);
      await axios.post(
        updateFarmStrainUrl(),
        {
          farmId: Number(managementPlanForm.farmId),
          strainId: Number(nextStrainId),
          name: planName,
          issuer: Number(managementPlanForm.farmId),
        },
        { headers: { "Content-Type": "application/json" } }
      );
      setManagementPlanSaveConfirmation(`${planName} has successfully been saved`);
      setManagementPlanStrainFile(JSON.parse(JSON.stringify(managementPlanOriginalStrainFile)));
      setManagementPlanPreviewFile({});
      handleCloseManagementPlanStrainDialog();
    } catch (error) {
      console.error("Failed to save management plan strain", error);
    } finally {
      setManagementPlanStrainSaving(false);
    }
  };

  useEffect(() => {
    if (
      !managementPlanStrainForm.day ||
      !managementPlanStrainForm.category ||
      !managementPlanStrainForm.parametre
    ) {
      return;
    }

    setManagementPlanStrainFile((strainFile) =>
      buildPreviewStrainFile(
        strainFile,
        managementPlanStrainForm,
        managementPlanActivityTypeOptions,
        managementPlanActivityNameOptions
      )
    );
  }, [
    managementPlanActivityNameOptions,
    managementPlanActivityTypeOptions,
    managementPlanStrainForm.day,
    managementPlanStrainForm.category,
    managementPlanStrainForm.parametre,
    managementPlanStrainForm.equality,
    managementPlanStrainForm.lowValue,
    managementPlanStrainForm.highValue,
    managementPlanStrainForm.enable,
    managementPlanStrainForm.activityCategory,
    managementPlanStrainForm.activityName,
    managementPlanStrainForm.startBy,
    managementPlanStrainForm.endBy,
    managementPlanStrainForm.dosage,
  ]);

  useEffect(() => {
    const selectedActivityType = managementPlanActivityTypeOptions.find(
      (option) => option.name === managementPlanStrainForm.activityCategory
    );
    const broilerDataType = activityTypeToBroilerDataType[selectedActivityType?.id];
    const farmId = managementPlanForm.farmId;

    if (!farmId || !broilerDataType) {
      setManagementPlanActivityNameOptions([]);
      return;
    }

    let isActive = true;
    setManagementPlanActivityNameLoading(true);
    axios
      .get(broilerDataByFarmAndTypeUrl(farmId, broilerDataType))
      .then((response) => {
        if (!isActive) return;
        const rows = Array.isArray(response.data) ? response.data : [];
        const options = rows
          .map((row) => {
            const id = pickFirst(row?.id, row?.ID);
            const name = pickFirst(row?.value, row?.Value);
            return id != null && name ? { id: String(id), name } : null;
          })
          .filter(Boolean);
        setManagementPlanActivityNameOptions(options);
      })
      .catch((error) => {
        if (!isActive) return;
        console.error("Failed to load scheduled activity names", error);
        setManagementPlanActivityNameOptions([]);
      })
      .finally(() => {
        if (isActive) {
          setManagementPlanActivityNameLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [
    managementPlanActivityTypeOptions,
    managementPlanForm.farmId,
    managementPlanStrainForm.activityCategory,
  ]);

  const handleManagementPlanStrainFieldChange = (key, value) => {
    setManagementPlanStrainForm((form) => {
      if (key === "day") {
        return {
          day: value,
          category: "",
          parametre: "",
          equality: "",
          lowValue: "",
          highValue: "",
          enable: "",
          planName: form.planName,
          activityCategory: form.activityCategory,
          activityName: form.activityName,
          startBy: form.startBy,
          endBy: form.endBy,
          dosage: form.dosage,
        };
      }

      if (key === "category") {
        if (value === "mortality") {
          return {
            ...form,
            category: value,
            parametre: MORTALITY_TARGET_PARAMETER,
            ...getMortalityTargetDefaults(
              managementPlanStrainFile?.[form.day]?.[value]?.[MORTALITY_TARGET_PARAMETER]
            ),
          };
        }
        return {
          ...form,
          category: value,
          parametre: "",
          equality: "",
          lowValue: "",
          highValue: "",
          enable: "",
        };
      }

      if (key === "activityCategory") {
        return {
          ...form,
          activityCategory: value,
          activityName: "",
        };
      }

      if (key === "parametre") {
        const parametreRecord = managementPlanStrainFile?.[form.day]?.[form.category]?.[value];
        const defaults = isMortalityTarget(form.category, value)
          ? getMortalityTargetDefaults(parametreRecord)
          : getStrainParameterDefaults(parametreRecord);
        return {
          ...form,
          parametre: value,
          ...defaults,
        };
      }

      if (key === "equality") {
        if (value === "=") {
          const sharedValue = form.lowValue || form.highValue;
          return { ...form, equality: value, lowValue: sharedValue, highValue: sharedValue };
        }
        if (value === "<") {
          return {
            ...form,
            equality: value,
            lowValue: "",
            highValue: form.highValue || form.lowValue,
          };
        }
        if (value === ">" || value === ">=") {
          return {
            ...form,
            equality: value,
            lowValue: form.lowValue || form.highValue,
            highValue: "",
          };
        }
        if (value === "record") {
          return {
            ...form,
            equality: value,
            lowValue: "",
            highValue: "",
            enable: form.enable || "true",
          };
        }
        return { ...form, equality: value };
      }

      if (key === "lowValue" || key === "highValue") {
        const nextForm = { ...form, [key]: value };
        if (
          nextForm.equality === "=" &&
          nextForm.lowValue !== "" &&
          nextForm.highValue !== "" &&
          nextForm.lowValue !== nextForm.highValue
        ) {
          nextForm.equality = "interval";
        }
        return nextForm;
      }

      if (key === "startBy" || key === "endBy") {
        return {
          ...form,
          [key]: formatScheduledTime(value),
        };
      }

      if (key === "dosage") {
        return {
          ...form,
          dosage: value.replace(/\D/g, ""),
        };
      }

      return {
        ...form,
        [key]: value,
      };
    });
  };

  const handleEditSelectedManagementPlan = async () => {
    const selectedPlan =
      managementPlanPlanOptions.find((option) => option.id === managementPlanForm.planOptionId) ??
      managementPlanPlanOptions.find(
        (option) =>
          String(option.farmId) === String(managementPlanForm.farmId) &&
          option.plan === managementPlanForm.plan
      );
    const strainId = selectedPlan?.strainId ?? selectedPlan?.sqlId;
    if (strainId == null) {
      console.error("Unable to load strain details: strainId was not found for selected plan.");
      return;
    }

    setManagementPlanStrainLoading(true);
    setManagementPlanStrainDialogOpen(true);
    try {
      const [response, activityTypesResponse] = await Promise.all([
        axios.get(strainDetailsUrl(strainId)),
        axios.get(actTypesUrl()),
      ]);
      const strainFile = getStrainFile(response.data);
      const dayOptions = sortDayOptions(getObjectKeys(strainFile));
      const activityTypeOptions = (
        Array.isArray(activityTypesResponse.data) ? activityTypesResponse.data : []
      )
        .map((activityType) => {
          const id = pickFirst(activityType?.id, activityType?.ID);
          const name = pickFirst(
            activityType?.ActivityName,
            activityType?.activityName,
            activityType?.name
          );
          return id != null && name ? { id: String(id), name } : null;
        })
        .filter(Boolean);
      const originalStrainFile = JSON.parse(JSON.stringify(strainFile));
      setManagementPlanOriginalStrainFile(originalStrainFile);
      setManagementPlanStrainFile(strainFile);
      setManagementPlanActivityTypeOptions(activityTypeOptions);
      setManagementPlanStrainForm({
        day: "",
        category: "",
        parametre: "",
        equality: "",
        lowValue: "",
        highValue: "",
        enable: "",
        planName: "",
        activityCategory: "",
        activityName: "",
        startBy: "",
        endBy: "",
        dosage: "",
      });
      setManagementPlanStrainOptions({
        days: dayOptions,
        categories: [],
        parametres: [],
        equalities: [],
        values: [],
      });
      if (dayOptions.length === 0) {
        console.error("Strain details response did not include a populated file object.");
      }
    } catch (error) {
      console.error("Failed to load strain details", error);
      setManagementPlanOriginalStrainFile({});
      setManagementPlanStrainFile({});
      setManagementPlanActivityTypeOptions([]);
      setManagementPlanStrainOptions({
        days: [],
        categories: [],
        parametres: [],
        equalities: [],
        values: [],
      });
    } finally {
      setManagementPlanStrainLoading(false);
    }
  };

  const handleCloseManagementPlanDialog = () => {
    setManagementPlanDialogOpen(false);
    setManagementPlanForm({ farmId: "", plan: "", planOptionId: "" });
    setManagementPlanEditingId(null);
    setManagementPlanSelectedIds([]);
  };

  const handleToggleManagementPlanSelection = (optionId) => {
    setManagementPlanSelectedIds((selectedIds) =>
      selectedIds.includes(optionId)
        ? selectedIds.filter((selectedId) => selectedId !== optionId)
        : [...selectedIds, optionId]
    );
  };

  const handleAddSelectedManagementPlans = async () => {
    const selectedOptions = managementPlanAddOptions.filter((option) =>
      managementPlanSelectedIds.includes(option.id)
    );
    if (selectedOptions.length === 0) return;

    setManagementPlanSaving(true);
    try {
      await Promise.all(
        selectedOptions.map((option) =>
          axios.post(
            updateFarmStrainUrl(),
            {
              farmId: Number(option.ownerId),
              strainId: Number(option.strainId),
              name: option.plan,
              issuer: Number(option.ownerId),
            },
            { headers: { "Content-Type": "application/json" } }
          )
        )
      );
      handleCloseManagementPlanDialog();
      await loadManagementPlanRows();
    } catch (error) {
      console.error("Failed to add management plans", error);
    } finally {
      setManagementPlanSaving(false);
    }
  };

  const handleSaveManagementPlanRow = async () => {
    const selectedFarm = managementPlanFarmOptions.find(
      (farm) => String(farm.id) === String(managementPlanForm.farmId)
    );
    if (!selectedFarm || !managementPlanForm.plan.trim()) return;

    const editingRow = managementPlanRows.find((row) => row.id === managementPlanEditingId);
    if (managementPlanEditingId != null && editingRow?.sqlId == null) {
      console.error("Unable to edit management plan: SQL row id is missing.");
      return;
    }

    const payload = {
      farmId: selectedFarm.id,
      type: 20,
      value: "",
      description: managementPlanForm.plan.trim(),
    };
    if (editingRow?.sqlId != null) {
      payload.id = editingRow.sqlId;
    }

    setManagementPlanSaving(true);
    try {
      await axios.post(updateBroilerDataUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
      handleCloseManagementPlanDialog();
      await loadManagementPlanRows();
    } catch (error) {
      console.error("Failed to save management plan", error);
    } finally {
      setManagementPlanSaving(false);
    }
  };

  const handleDeleteManagementPlanRow = async (row, confirmed = false) => {
    if (managementPlanSaving) return;
    if (row?.sqlId == null) {
      console.error("Unable to delete management plan: SQL row id is missing.");
      return;
    }
    if (row?.issuer == null || row?.farmId == null || String(row.issuer) !== String(row.farmId)) {
      setManagementPlanDeleteMessage("Only self-generated plans can be deleted");
      return;
    }

    if (!confirmed) {
      setManagementPlanPendingDelete(row);
      return;
    }

    setManagementPlanSaving(true);
    try {
      const response = await axios.post(
        removeFarmStrainUrl(),
        {
          id: Number(row.sqlId),
          farmId: Number(row.farmId),
          strainId: Number(row.strainId),
          name: row.plan,
          issuer: Number(row.issuer),
        },
        { headers: { "Content-Type": "application/json" } }
      );
      if (isApiErrorResponse(response.data)) {
        throw new Error(response.data?.message || "Failed to delete management plan");
      }
      await loadManagementPlanRows();
    } catch (error) {
      console.error("Failed to delete management plan", error);
      setManagementPlanDeleteMessage("Failed to delete management plan");
    } finally {
      setManagementPlanPendingDelete(null);
      setManagementPlanSaving(false);
    }
  };

  const handleSupplierSort = (orderBy) => {
    setSupplierSort((sort) => ({
      orderBy,
      order: sort.orderBy === orderBy && sort.order === "asc" ? "desc" : "asc",
    }));
  };

  const sortedSupplierRows = [...supplierRows].sort((a, b) => {
    const left = String(a[supplierSort.orderBy] ?? "");
    const right = String(b[supplierSort.orderBy] ?? "");
    const result = left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
    return supplierSort.order === "asc" ? result : -result;
  });

  const handleBreederSort = (orderBy) => {
    setBreederSort((sort) => ({
      orderBy,
      order: sort.orderBy === orderBy && sort.order === "asc" ? "desc" : "asc",
    }));
  };

  const sortedBreederRows = [...breederRows].sort((a, b) => {
    const left = String(a[breederSort.orderBy] ?? "");
    const right = String(b[breederSort.orderBy] ?? "");
    const result = left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
    return breederSort.order === "asc" ? result : -result;
  });

  const handleOpenFeedStageDialog = () => {
    setFeedStageForm({ stage: "" });
    setFeedStageEditingId(null);
    setFeedStageDialogOpen(true);
  };

  const handleOpenFeedStageEditDialog = (row) => {
    setFeedStageForm({ stage: row.stage ?? "" });
    setFeedStageEditingId(row.id);
    setFeedStageDialogOpen(true);
  };

  const handleCloseFeedStageDialog = () => {
    setFeedStageDialogOpen(false);
    setFeedStageEditingId(null);
    setFeedStageForm({ stage: "" });
  };

  const handleSaveFeedStageRow = async () => {
    const farmId = resolveFarmId();

    if (farmId == null) {
      console.error("Unable to save feed stage: farmId was not found in login or broiler cache.");
      return;
    }

    const payload = {
      farmId,
      type: 10,
      value: feedStageForm.stage,
      description: "",
    };
    const editingRow = feedStageRows.find((row) => row.id === feedStageEditingId);
    if (feedStageEditingId != null && editingRow?.sqlId == null) {
      console.error("Unable to edit feed stage: SQL row id is missing.");
      return;
    }
    if (editingRow?.sqlId != null) {
      payload.id = editingRow.sqlId;
    }

    setFeedStageSaving(true);
    try {
      const response = await axios.post(updateBroilerDataUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
      if (feedStageEditingId == null) {
        const responseId = getRecordId(response?.data);
        setFeedStageRows((rows) => [
          ...rows,
          {
            id: `feed-stage-${Date.now()}-${rows.length}`,
            sqlId: responseId,
            stage: feedStageForm.stage,
          },
        ]);
      }
      await loadFeedStageRows();
      handleCloseFeedStageDialog();
    } catch (error) {
      console.error("Failed to save feed stage", error);
    } finally {
      setFeedStageSaving(false);
    }
  };

  const handleDeleteFeedStageRow = async (row) => {
    if (row?.sqlId == null) {
      console.error("Unable to delete feed stage: SQL row id is missing.");
      return;
    }

    try {
      await axios.post(
        removeBroilerDataUrl(),
        { id: row.sqlId },
        { headers: { "Content-Type": "application/json" } }
      );
      await loadFeedStageRows();
    } catch (error) {
      console.error("Failed to delete feed stage", error);
    }
  };

  const handleFeedStageSort = () => {
    setFeedStageSort((sort) => ({
      orderBy: "stage",
      order: sort.order === "asc" ? "desc" : "asc",
    }));
  };

  const sortedFeedStageRows = [...feedStageRows].sort((a, b) => {
    const left = String(a[feedStageSort.orderBy] ?? "");
    const right = String(b[feedStageSort.orderBy] ?? "");
    const result = left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
    return feedStageSort.order === "asc" ? result : -result;
  });

  const handleOpenFeedNameDialog = () => {
    setFeedNameForm({ name: "", description: "" });
    setFeedNameEditingId(null);
    setFeedNameDialogOpen(true);
  };

  const handleOpenFeedNameEditDialog = (row) => {
    setFeedNameForm({ name: row.name ?? "", description: row.description ?? "" });
    setFeedNameEditingId(row.id);
    setFeedNameDialogOpen(true);
  };

  const handleCloseFeedNameDialog = () => {
    setFeedNameDialogOpen(false);
    setFeedNameEditingId(null);
    setFeedNameForm({ name: "", description: "" });
  };

  const handleSaveFeedNameRow = async () => {
    const farmId = resolveFarmId();

    if (farmId == null) {
      console.error("Unable to save feed name: farmId was not found in login or broiler cache.");
      return;
    }

    const payload = {
      farmId,
      type: 5,
      value: feedNameForm.name,
      description: feedNameForm.description,
    };
    const editingRow = feedNameRows.find((row) => row.id === feedNameEditingId);
    if (feedNameEditingId != null && editingRow?.sqlId == null) {
      console.error("Unable to edit feed name: SQL row id is missing.");
      return;
    }
    if (editingRow?.sqlId != null) {
      payload.id = editingRow.sqlId;
    }

    setFeedNameSaving(true);
    try {
      const response = await axios.post(updateBroilerDataUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
      if (feedNameEditingId == null) {
        const responseId = getRecordId(response?.data);
        setFeedNameRows((rows) => [
          ...rows,
          {
            id: `feed-name-${Date.now()}-${rows.length}`,
            sqlId: responseId,
            name: feedNameForm.name,
            description: feedNameForm.description,
          },
        ]);
      }
      await loadFeedNameRows();
      handleCloseFeedNameDialog();
    } catch (error) {
      console.error("Failed to save feed name", error);
    } finally {
      setFeedNameSaving(false);
    }
  };

  const handleDeleteFeedNameRow = async (row) => {
    if (row?.sqlId == null) {
      console.error("Unable to delete feed name: SQL row id is missing.");
      return;
    }

    try {
      await axios.post(
        removeBroilerDataUrl(),
        { id: row.sqlId },
        { headers: { "Content-Type": "application/json" } }
      );
      await loadFeedNameRows();
    } catch (error) {
      console.error("Failed to delete feed name", error);
    }
  };

  const handleFeedNameSort = (orderBy) => {
    setFeedNameSort((sort) => ({
      orderBy,
      order: sort.orderBy === orderBy && sort.order === "asc" ? "desc" : "asc",
    }));
  };

  const sortedFeedNameRows = [...feedNameRows].sort((a, b) => {
    const left = String(a[feedNameSort.orderBy] ?? "");
    const right = String(b[feedNameSort.orderBy] ?? "");
    const result = left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
    return feedNameSort.order === "asc" ? result : -result;
  });

  const handleOpenFeedSupplierDialog = () => {
    setFeedSupplierForm({ name: "", description: "" });
    setFeedSupplierEditingId(null);
    setFeedSupplierDialogOpen(true);
  };

  const handleOpenFeedSupplierEditDialog = (row) => {
    setFeedSupplierForm({ name: row.name ?? "", description: row.description ?? "" });
    setFeedSupplierEditingId(row.id);
    setFeedSupplierDialogOpen(true);
  };

  const handleCloseFeedSupplierDialog = () => {
    setFeedSupplierDialogOpen(false);
    setFeedSupplierEditingId(null);
    setFeedSupplierForm({ name: "", description: "" });
  };

  const handleSaveFeedSupplierRow = async () => {
    const farmId = resolveFarmId();

    if (farmId == null) {
      console.error(
        "Unable to save feed supplier: farmId was not found in login or broiler cache."
      );
      return;
    }

    const payload = {
      farmId,
      type: 11,
      value: feedSupplierForm.name,
      description: feedSupplierForm.description,
    };
    const editingRow = feedSupplierRows.find((row) => row.id === feedSupplierEditingId);
    if (feedSupplierEditingId != null && editingRow?.sqlId == null) {
      console.error("Unable to edit feed supplier: SQL row id is missing.");
      return;
    }
    if (editingRow?.sqlId != null) {
      payload.id = editingRow.sqlId;
    }

    setFeedSupplierSaving(true);
    try {
      const response = await axios.post(updateBroilerDataUrl(), payload, {
        headers: { "Content-Type": "application/json" },
      });
      if (feedSupplierEditingId == null) {
        const responseId = getRecordId(response?.data);
        setFeedSupplierRows((rows) => [
          ...rows,
          {
            id: `feed-supplier-${Date.now()}-${rows.length}`,
            sqlId: responseId,
            name: feedSupplierForm.name,
            description: feedSupplierForm.description,
          },
        ]);
      }
      await loadFeedSupplierRows();
      handleCloseFeedSupplierDialog();
    } catch (error) {
      console.error("Failed to save feed supplier", error);
    } finally {
      setFeedSupplierSaving(false);
    }
  };

  const handleDeleteFeedSupplierRow = async (row) => {
    if (row?.sqlId == null) {
      console.error("Unable to delete feed supplier: SQL row id is missing.");
      return;
    }

    try {
      await axios.post(
        removeBroilerDataUrl(),
        { id: row.sqlId },
        { headers: { "Content-Type": "application/json" } }
      );
      await loadFeedSupplierRows();
    } catch (error) {
      console.error("Failed to delete feed supplier", error);
    }
  };

  const handleFeedSupplierSort = (orderBy) => {
    setFeedSupplierSort((sort) => ({
      orderBy,
      order: sort.orderBy === orderBy && sort.order === "asc" ? "desc" : "asc",
    }));
  };

  const sortedFeedSupplierRows = [...feedSupplierRows].sort((a, b) => {
    const left = String(a[feedSupplierSort.orderBy] ?? "");
    const right = String(b[feedSupplierSort.orderBy] ?? "");
    const result = left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
    return feedSupplierSort.order === "asc" ? result : -result;
  });

  const handleOpenHealthItemDialog = (section) => {
    setHealthItemSection(section);
    setHealthItemForm({ name: "", description: "" });
    setHealthItemEditingId(null);
    setHealthItemDialogOpen(true);
  };

  const handleOpenHealthItemEditDialog = (section, row) => {
    setHealthItemSection(section);
    setHealthItemForm({ name: row.name ?? "", description: row.description ?? "" });
    setHealthItemEditingId(row.id);
    setHealthItemDialogOpen(true);
  };

  const handleCloseHealthItemDialog = () => {
    setHealthItemDialogOpen(false);
    setHealthItemSection("");
    setHealthItemEditingId(null);
    setHealthItemForm({ name: "", description: "" });
  };

  const handleSaveHealthItemRow = async () => {
    if (!healthItemSection) return;

    const type = healthTypeBySection[healthItemSection];
    if (type != null) {
      const farmId = resolveFarmId();

      if (farmId == null) {
        console.error(
          `Unable to save ${healthItemSection}: farmId was not found in login or broiler cache.`
        );
        return;
      }

      const payload = {
        farmId,
        type,
        value: healthItemForm.name,
        description: healthItemForm.description,
      };
      const editingRow = (healthRows[healthItemSection] ?? []).find(
        (row) => row.id === healthItemEditingId
      );
      if (healthItemEditingId != null && editingRow?.sqlId == null) {
        console.error(`Unable to edit ${healthItemSection}: SQL row id is missing.`);
        return;
      }
      if (editingRow?.sqlId != null) {
        payload.id = editingRow.sqlId;
      }

      setHealthSaving(true);
      try {
        const response = await axios.post(updateBroilerDataUrl(), payload, {
          headers: { "Content-Type": "application/json" },
        });
        if (healthItemEditingId == null) {
          const responseId = getRecordId(response?.data);
          setHealthRows((rows) => {
            const sectionRows = rows[healthItemSection] ?? [];
            return {
              ...rows,
              [healthItemSection]: [
                ...sectionRows,
                {
                  id: `health-${healthItemSection}-${Date.now()}-${sectionRows.length}`,
                  sqlId: responseId,
                  name: healthItemForm.name,
                  description: healthItemForm.description,
                },
              ],
            };
          });
        }
        await loadHealthRows(healthItemSection);
        handleCloseHealthItemDialog();
      } catch (error) {
        console.error(`Failed to save ${healthItemSection}`, error);
      } finally {
        setHealthSaving(false);
      }
      return;
    }

    setHealthRows((rows) => {
      const sectionRows = rows[healthItemSection] ?? [];
      const nextSectionRows =
        healthItemEditingId == null
          ? [
              ...sectionRows,
              {
                id: `health-${healthItemSection}-${Date.now()}-${sectionRows.length}`,
                name: healthItemForm.name,
                description: healthItemForm.description,
              },
            ]
          : sectionRows.map((row) =>
              row.id === healthItemEditingId
                ? { ...row, name: healthItemForm.name, description: healthItemForm.description }
                : row
            );

      return { ...rows, [healthItemSection]: nextSectionRows };
    });
    handleCloseHealthItemDialog();
  };

  const handleDeleteHealthItemRow = async (section, row) => {
    const type = healthTypeBySection[section];
    if (type != null) {
      if (row?.sqlId == null) {
        console.error(`Unable to delete ${section}: SQL row id is missing.`);
        return;
      }

      try {
        await axios.post(
          removeBroilerDataUrl(),
          { id: row.sqlId },
          { headers: { "Content-Type": "application/json" } }
        );
        await loadHealthRows(section);
      } catch (error) {
        console.error(`Failed to delete ${section}`, error);
      }
      return;
    }

    setHealthRows((rows) => ({
      ...rows,
      [section]: (rows[section] ?? []).filter((currentRow) => currentRow.id !== row.id),
    }));
  };

  const handleHealthSort = (section, orderBy) => {
    setHealthSort((sorts) => {
      const current = sorts[section] ?? { orderBy: "name", order: "asc" };
      return {
        ...sorts,
        [section]: {
          orderBy,
          order: current.orderBy === orderBy && current.order === "asc" ? "desc" : "asc",
        },
      };
    });
  };

  const getSortedHealthRows = (section) => {
    const sort = healthSort[section] ?? { orderBy: "name", order: "asc" };
    return [...(healthRows[section] ?? [])].sort((a, b) => {
      const left = String(a[sort.orderBy] ?? "");
      const right = String(b[sort.orderBy] ?? "");
      const result = left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
      return sort.order === "asc" ? result : -result;
    });
  };

  const loadSystemRows = useCallback(async (section) => {
    const type = systemTypeBySection[section];
    if (type == null) return;

    const farmId = resolveFarmId();

    if (farmId == null) {
      console.error(`Unable to load ${section}: farmId was not found in login or broiler cache.`);
      setSystemRows((rows) => ({ ...rows, [section]: [] }));
      return;
    }

    setSystemLoading((loading) => ({ ...loading, [section]: true }));
    try {
      const response = await axios.get(broilerDataByFarmUrl(farmId));
      const rows = Array.isArray(response.data) ? response.data : [];
      setSystemRows((currentRows) => ({
        ...currentRows,
        [section]: rows
          .filter((row) => Number(row?.type) === type)
          .map((row, index) => {
            const sqlId = getRecordId(row);
            return {
              id: `system-${section}-${sqlId ?? index}`,
              sqlId,
              makeModel: row?.value ?? "",
              description: row?.description ?? "",
            };
          }),
      }));
    } catch (error) {
      console.error(`Failed to load ${section}`, error);
    } finally {
      setSystemLoading((loading) => ({ ...loading, [section]: false }));
    }
  }, []);

  useEffect(() => {
    if (systemsDialogOpen) {
      Object.keys(systemTypeBySection).forEach((section) => {
        loadSystemRows(section);
      });
    }
  }, [systemsDialogOpen, loadSystemRows]);

  const handleOpenSystemItemDialog = (section) => {
    setSystemItemSection(section);
    setSystemItemForm({ makeModel: "", description: "" });
    setSystemItemEditingId(null);
    setSystemItemDialogOpen(true);
  };

  const handleOpenSystemItemEditDialog = (section, row) => {
    setSystemItemSection(section);
    setSystemItemForm({ makeModel: row.makeModel ?? "", description: row.description ?? "" });
    setSystemItemEditingId(row.id);
    setSystemItemDialogOpen(true);
  };

  const handleCloseSystemItemDialog = () => {
    setSystemItemDialogOpen(false);
    setSystemItemSection("");
    setSystemItemEditingId(null);
    setSystemItemForm({ makeModel: "", description: "" });
  };

  const handleSaveSystemItemRow = async () => {
    if (!systemItemSection) return;

    const type = systemTypeBySection[systemItemSection];
    if (type != null) {
      const farmId = resolveFarmId();

      if (farmId == null) {
        console.error(
          `Unable to save ${systemItemSection}: farmId was not found in login or broiler cache.`
        );
        return;
      }

      const payload = {
        farmId,
        type,
        value: systemItemForm.makeModel,
        description: systemItemForm.description,
      };
      const editingRow = (systemRows[systemItemSection] ?? []).find(
        (row) => row.id === systemItemEditingId
      );
      if (systemItemEditingId != null && editingRow?.sqlId == null) {
        console.error(`Unable to edit ${systemItemSection}: SQL row id is missing.`);
        return;
      }
      if (editingRow?.sqlId != null) {
        payload.id = editingRow.sqlId;
      }

      setSystemSaving(true);
      try {
        const response = await axios.post(updateBroilerDataUrl(), payload, {
          headers: { "Content-Type": "application/json" },
        });
        if (systemItemEditingId == null) {
          const responseId = getRecordId(response?.data);
          setSystemRows((rows) => {
            const sectionRows = rows[systemItemSection] ?? [];
            return {
              ...rows,
              [systemItemSection]: [
                ...sectionRows,
                {
                  id: `system-${systemItemSection}-${Date.now()}-${sectionRows.length}`,
                  sqlId: responseId,
                  makeModel: systemItemForm.makeModel,
                  description: systemItemForm.description,
                },
              ],
            };
          });
        }
        await loadSystemRows(systemItemSection);
        handleCloseSystemItemDialog();
      } catch (error) {
        console.error(`Failed to save ${systemItemSection}`, error);
      } finally {
        setSystemSaving(false);
      }
      return;
    }

    setSystemRows((rows) => {
      const sectionRows = rows[systemItemSection] ?? [];
      const nextSectionRows =
        systemItemEditingId == null
          ? [
              ...sectionRows,
              {
                id: `system-${systemItemSection}-${Date.now()}-${sectionRows.length}`,
                makeModel: systemItemForm.makeModel,
                description: systemItemForm.description,
              },
            ]
          : sectionRows.map((row) =>
              row.id === systemItemEditingId
                ? {
                    ...row,
                    makeModel: systemItemForm.makeModel,
                    description: systemItemForm.description,
                  }
                : row
            );

      return { ...rows, [systemItemSection]: nextSectionRows };
    });
    handleCloseSystemItemDialog();
  };

  const handleDeleteSystemItemRow = async (section, row) => {
    const type = systemTypeBySection[section];
    if (type != null) {
      if (row?.sqlId == null) {
        console.error(`Unable to delete ${section}: SQL row id is missing.`);
        return;
      }

      try {
        await axios.post(
          removeBroilerDataUrl(),
          { id: row.sqlId },
          { headers: { "Content-Type": "application/json" } }
        );
        await loadSystemRows(section);
      } catch (error) {
        console.error(`Failed to delete ${section}`, error);
      }
      return;
    }

    setSystemRows((rows) => ({
      ...rows,
      [section]: (rows[section] ?? []).filter((currentRow) => currentRow.id !== row.id),
    }));
  };

  const handleSystemSort = (section, orderBy) => {
    setSystemSort((sorts) => {
      const current = sorts[section] ?? { orderBy: "makeModel", order: "asc" };
      return {
        ...sorts,
        [section]: {
          orderBy,
          order: current.orderBy === orderBy && current.order === "asc" ? "desc" : "asc",
        },
      };
    });
  };

  const getSortedSystemRows = (section) => {
    const sort = systemSort[section] ?? { orderBy: "makeModel", order: "asc" };
    return [...(systemRows[section] ?? [])].sort((a, b) => {
      const left = String(a[sort.orderBy] ?? "");
      const right = String(b[sort.orderBy] ?? "");
      const result = left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
      return sort.order === "asc" ? result : -result;
    });
  };

  const selectedStrainDay = managementPlanStrainFile?.[managementPlanStrainForm.day];
  const selectedStrainCategory = selectedStrainDay?.[managementPlanStrainForm.category];
  const managementPlanStrainFieldOptions = {
    days: managementPlanStrainOptions.days,
    categories: getObjectKeys(selectedStrainDay).filter((category) => {
      const normalizedCategory = String(category).toLowerCase();
      return !normalizedCategory.includes("task") && !normalizedCategory.includes("schedule");
    }),
    parametres:
      managementPlanStrainForm.category === "mortality"
        ? [...new Set([...getObjectKeys(selectedStrainCategory), MORTALITY_TARGET_PARAMETER])]
        : getObjectKeys(selectedStrainCategory),
    equalities: isMortalityTarget(
      managementPlanStrainForm.category,
      managementPlanStrainForm.parametre
    )
      ? ["interval"]
      : getStrainEqualityOptions(managementPlanStrainForm.equality),
    enable: ENABLE_OPTIONS,
  };
  const managementPlanStrainFieldDisabled = {
    lowValue:
      managementPlanStrainLoading ||
      managementPlanStrainSaving ||
      !["=", ">", ">=", "interval"].includes(managementPlanStrainForm.equality),
    highValue:
      managementPlanStrainLoading ||
      managementPlanStrainSaving ||
      !["=", "<", "interval"].includes(managementPlanStrainForm.equality),
    enable:
      managementPlanStrainLoading ||
      managementPlanStrainSaving ||
      managementPlanStrainForm.equality !== "record",
  };
  const canAddScheduledActivity =
    Boolean(managementPlanStrainForm.day) &&
    Boolean(
      getSchedulePreviewValues(
        managementPlanStrainForm,
        managementPlanActivityTypeOptions,
        managementPlanActivityNameOptions
      )
    ) &&
    Boolean(managementPlanStrainForm.startBy) &&
    Boolean(managementPlanStrainForm.endBy) &&
    Boolean(managementPlanStrainForm.dosage);
  const managementPlanFacilityPreviewRows = mapPreviewRows(
    managementPlanPreviewFile,
    managementPlanStrainForm
  );
  const managementPlanScheduledPreviewRows =
    mapScheduledActivityPreviewRows(managementPlanPreviewFile);
  const managementPlanPreviewRows =
    managementPlanPreviewPage === "facility"
      ? managementPlanFacilityPreviewRows
      : managementPlanScheduledPreviewRows;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MDBox px={1}>
              <MDTypography variant="h4" fontWeight="medium">
                Facilities
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid container item spacing={6}>
            {facilitySections.map(({ title, links }) => (
              <Grid item xs={12} md={6} key={title}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {title}
                  </Typography>
                  {links.map(({ label, to }, index) => (
                    <Button
                      fullWidth
                      variant="outlined"
                      component={
                        label === "DOCs" ||
                        label === "Feed" ||
                        label === "Health" ||
                        label === "Production Cycle" ||
                        label === "Management Plans" ||
                        label === "Production Systems"
                          ? "button"
                          : RouterLink
                      }
                      to={
                        label === "DOCs" ||
                        label === "Feed" ||
                        label === "Health" ||
                        label === "Production Cycle" ||
                        label === "Management Plans" ||
                        label === "Production Systems"
                          ? undefined
                          : to
                      }
                      onClick={
                        label === "DOCs"
                          ? () => setDocsDialogOpen(true)
                          : label === "Feed"
                          ? () => setFeedDialogOpen(true)
                          : label === "Health"
                          ? () => setHealthDialogOpen(true)
                          : label === "Production Cycle"
                          ? () => setProductionCycleDialogOpen(true)
                          : label === "Management Plans"
                          ? () => setManagementPlansDialogOpen(true)
                          : label === "Production Systems"
                          ? () => setSystemsDialogOpen(true)
                          : undefined
                      }
                      sx={{
                        mt: index === 0 ? 0 : 2,
                        color: "grey.600",
                        ...(label === "DOCs" ? { textTransform: "none" } : {}),
                      }}
                      key={label}
                    >
                      {label}
                    </Button>
                  ))}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </MDBox>
      <Dialog
        open={productionCycleDialogOpen}
        onClose={() => setProductionCycleDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox component="span" sx={{ fontWeight: 600 }}>
              Cycle
            </MDBox>
            <IconButton
              aria-label="close cycle"
              size="small"
              onClick={() => setProductionCycleDialogOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <SectionTitle>Production Cycles</SectionTitle>
          <Paper variant="outlined" sx={{ pb: "0.3cm", borderRadius: 0 }}>
            <MDBox
              sx={{
                mt: "0.3cm",
                ml: "0.5cm",
                width: "calc(100% - 0.5cm)",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "0.2cm",
                overflow: "hidden",
              }}
            >
              <TableContainer component={MDBox} sx={{ boxShadow: "none" }}>
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "17.28cm",
                    minWidth: "17.28cm",
                    borderCollapse: "collapse",
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "grey.300",
                      fontSize: "0.8rem",
                      lineHeight: 1.2,
                      p: 0.75,
                      textAlign: "center",
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "7.2cm" }} />
                    <col style={{ width: "7.2cm" }} />
                    <col style={{ width: "2.88cm" }} />
                  </colgroup>
                  <TableBody>
                    <TableRow sx={{ height: "1.5cm", bgcolor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active
                          direction={
                            productionCycleSort.orderBy === "facility"
                              ? productionCycleSort.order
                              : "asc"
                          }
                          onClick={() => handleProductionCycleSort("facility")}
                          hideSortIcon={false}
                          sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                        >
                          Facility
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active
                          direction={
                            productionCycleSort.orderBy === "cycleName"
                              ? productionCycleSort.order
                              : "asc"
                          }
                          onClick={() => handleProductionCycleSort("cycleName")}
                          hideSortIcon={false}
                          sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                        >
                          Cycle Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <IconButton
                          size="small"
                          onClick={handleOpenProductionCycleAddDialog}
                          aria-label="add production cycle"
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.800",
                            "&:hover": { bgcolor: "grey.200" },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {productionCycleLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          Loading production cycles...
                        </TableCell>
                      </TableRow>
                    ) : productionCycleRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          No data
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedProductionCycleRows.map((row) => (
                        <TableRow key={row.id} sx={{ "& td": { fontSize: "0.85rem" } }}>
                          <TableCell>{row.facility}</TableCell>
                          <TableCell>{row.cycleName}</TableCell>
                          <TableCell>
                            <MDBox
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                justifyContent: "center",
                                width: "100%",
                              }}
                            >
                              <EditIcon
                                onClick={() => handleOpenProductionCycleEditDialog(row)}
                                sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                              />
                              <DeleteIcon
                                onClick={() => handleDeleteProductionCycleRow(row)}
                                sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                              />
                            </MDBox>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          </Paper>
        </DialogContent>
      </Dialog>
      <Dialog
        open={productionCycleAddDialogOpen}
        onClose={handleCloseProductionCycleAddDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          {productionCycleEditingId == null ? "Add Production Cycle" : "Edit Production Cycle"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Facility"
                value={productionCycleForm.facilityId}
                onChange={(event) =>
                  setProductionCycleForm((form) => ({
                    ...form,
                    facilityId: event.target.value,
                  }))
                }
                fullWidth
                size="medium"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 40,
                  },
                  "& .MuiSelect-select": {
                    minHeight: "1.4375em !important",
                    display: "flex",
                    alignItems: "center",
                    py: "8.5px",
                  },
                }}
              >
                {productionCycleFacilityOptions.length === 0 ? (
                  <MenuItem value="" disabled>
                    No facilities
                  </MenuItem>
                ) : (
                  productionCycleFacilityOptions.map((facility) => (
                    <MenuItem key={facility.id} value={facility.id}>
                      {facility.name}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Cycle Name"
                value={productionCycleForm.cycleName}
                onChange={(event) =>
                  setProductionCycleForm((form) => ({
                    ...form,
                    cycleName: event.target.value,
                  }))
                }
                fullWidth
                size="medium"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: "calc(40px + 0.3mm)",
                  },
                  "& .MuiInputBase-input": {
                    py: "calc(8.5px + 0.2mm)",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="flex-end" gap={1}>
                <Button
                  variant="text"
                  color="dark"
                  onClick={handleCloseProductionCycleAddDialog}
                  disabled={productionCycleSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAddProductionCycleRow}
                  disabled={
                    productionCycleSaving ||
                    !productionCycleForm.facilityId ||
                    !productionCycleForm.cycleName.trim()
                  }
                  sx={{ color: "#fff" }}
                >
                  {productionCycleEditingId == null ? "Add" : "Save"}
                </Button>
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog
        open={managementPlansDialogOpen}
        onClose={() => setManagementPlansDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox component="span" sx={{ fontWeight: 600 }}>
              Management Plans
            </MDBox>
            <IconButton
              aria-label="close management plans"
              size="small"
              onClick={() => setManagementPlansDialogOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <SectionTitle>Management Plans</SectionTitle>
          <Paper variant="outlined" sx={{ pb: "0.3cm", borderRadius: 0 }}>
            <MDBox
              sx={{
                mt: "0.3cm",
                ml: "0.5cm",
                width: "calc(100% - 0.5cm)",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "0.2cm",
                overflow: "hidden",
              }}
            >
              <TableContainer component={MDBox} sx={{ boxShadow: "none" }}>
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "17.28cm",
                    minWidth: "17.28cm",
                    borderCollapse: "collapse",
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "grey.300",
                      fontSize: "0.8rem",
                      lineHeight: 1.2,
                      p: 0.75,
                      textAlign: "center",
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "7.2cm" }} />
                    <col style={{ width: "7.2cm" }} />
                    <col style={{ width: "2.88cm" }} />
                  </colgroup>
                  <TableBody>
                    <TableRow sx={{ height: "1.5cm", bgcolor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active
                          direction={
                            managementPlanSort.orderBy === "farm" ? managementPlanSort.order : "asc"
                          }
                          onClick={() => handleManagementPlanSort("farm")}
                          hideSortIcon={false}
                          sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                        >
                          Farm
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active
                          direction={
                            managementPlanSort.orderBy === "plan" ? managementPlanSort.order : "asc"
                          }
                          onClick={() => handleManagementPlanSort("plan")}
                          hideSortIcon={false}
                          sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                        >
                          Plan
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <IconButton
                          size="small"
                          onClick={handleOpenManagementPlanDialog}
                          aria-label="add management plan"
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.800",
                            "&:hover": { bgcolor: "grey.200" },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {managementPlanLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          Loading management plans...
                        </TableCell>
                      </TableRow>
                    ) : managementPlanRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          No data
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedManagementPlanRows.map((row) => (
                        <TableRow key={row.id} sx={{ "& td": { fontSize: "0.85rem" } }}>
                          <TableCell>{row.farm}</TableCell>
                          <TableCell>{row.plan}</TableCell>
                          <TableCell>
                            <MDBox
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                justifyContent: "center",
                                width: "100%",
                              }}
                            >
                              <EditIcon
                                onClick={() => handleOpenManagementPlanEditDialog(row)}
                                sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                              />
                              <DeleteIcon
                                onClick={() => handleDeleteManagementPlanRow(row)}
                                sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                              />
                              <Tooltip title="View plan parameters">
                                <IconButton
                                  size="small"
                                  aria-label={`view ${row.plan} parameters`}
                                  onClick={() => setViewManagementPlan(row)}
                                  sx={{ p: 0 }}
                                >
                                  <VisibilityIcon sx={{ fontSize: 28, color: "text.secondary" }} />
                                </IconButton>
                              </Tooltip>
                            </MDBox>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          </Paper>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(managementPlanPendingDelete)}
        onClose={() => {
          if (!managementPlanSaving) setManagementPlanPendingDelete(null);
        }}
        fullWidth
        maxWidth="xs"
        aria-labelledby="delete-management-plan-title"
        aria-describedby="delete-management-plan-description"
      >
        <DialogTitle id="delete-management-plan-title">Delete Management Plan</DialogTitle>
        <DialogContent>
          <Typography id="delete-management-plan-description" variant="body2">
            Are you sure you want to delete this plan?
          </Typography>
          <MDBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
            <Button
              autoFocus
              variant="text"
              color="dark"
              disabled={managementPlanSaving}
              onClick={() => setManagementPlanPendingDelete(null)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={managementPlanSaving}
              onClick={() => handleDeleteManagementPlanRow(managementPlanPendingDelete, true)}
              sx={{
                color: "#fff",
                bgcolor: "warning.main",
                minWidth: 96,
                "&:hover": { bgcolor: "warning.focus", color: "#fff" },
              }}
            >
              Delete
            </Button>
          </MDBox>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(managementPlanDeleteMessage)}
        onClose={() => setManagementPlanDeleteMessage("")}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox component="span">Delete Management Plan</MDBox>
            <IconButton
              aria-label="close delete management plan message"
              size="small"
              onClick={() => setManagementPlanDeleteMessage("")}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">{managementPlanDeleteMessage}</Typography>
          <MDBox display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setManagementPlanDeleteMessage("")}
              sx={{ color: "#fff", minWidth: 96 }}
            >
              OK
            </Button>
          </MDBox>
        </DialogContent>
      </Dialog>
      <Dialog
        open={managementPlanDialogOpen}
        onClose={handleCloseManagementPlanDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox component="span">
              {managementPlanDialogMode === "add"
                ? "Available Management Plans"
                : "Edit Management Plan"}
            </MDBox>
            <IconButton
              aria-label="close management plan"
              size="small"
              onClick={handleCloseManagementPlanDialog}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {managementPlanDialogMode === "add" ? (
            <Grid container spacing={2} pt={1}>
              <Grid item xs={12} md={6.9} sx={{ mx: "auto" }}>
                <Paper variant="outlined" sx={{ maxHeight: "9cm", overflow: "auto", p: 1 }}>
                  {managementPlanAddLoading ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      Loading plans...
                    </Typography>
                  ) : managementPlanAddOptions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      No plans
                    </Typography>
                  ) : (
                    managementPlanAddOptions.map((option) => (
                      <MDBox
                        key={option.id}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{
                          borderBottom: "1px solid",
                          borderColor: "grey.200",
                          minHeight: 40,
                          px: 1,
                          "&:last-of-type": { borderBottom: 0 },
                        }}
                      >
                        <Checkbox
                          checked={managementPlanSelectedIds.includes(option.id)}
                          onChange={() => handleToggleManagementPlanSelection(option.id)}
                          size="small"
                        />
                        <Typography variant="body2">{option.plan}</Typography>
                      </MDBox>
                    ))
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <MDBox display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                  <Button
                    variant="text"
                    color="dark"
                    onClick={handleCloseManagementPlanDialog}
                    disabled={managementPlanSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddSelectedManagementPlans}
                    disabled={
                      managementPlanSaving ||
                      managementPlanAddLoading ||
                      managementPlanSelectedIds.length === 0
                    }
                    sx={{ color: "#fff", minWidth: 96 }}
                  >
                    Add
                  </Button>
                </MDBox>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2} pt={1}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Plan"
                  fullWidth
                  size="medium"
                  value={managementPlanForm.planOptionId || ""}
                  onChange={(event) => {
                    const selectedOption = managementPlanPlanOptions.find(
                      (option) => option.id === event.target.value
                    );
                    setManagementPlanForm((form) => ({
                      ...form,
                      farmId: selectedOption?.farmId != null ? String(selectedOption.farmId) : "",
                      plan: selectedOption?.plan ?? "",
                      planOptionId: selectedOption?.id ?? "",
                    }));
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 40,
                    },
                    "& .MuiSelect-select": {
                      minHeight: "1.4375em !important",
                      display: "flex",
                      alignItems: "center",
                      py: "8.5px",
                    },
                  }}
                >
                  {managementPlanPlanOptions.length === 0 ? (
                    <MenuItem value="" disabled>
                      No plans
                    </MenuItem>
                  ) : (
                    managementPlanPlanOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.plan}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  sx={{ minHeight: "calc(40px + 0.3mm)" }}
                >
                  <Button
                    variant={managementPlanForm.plan ? "contained" : "outlined"}
                    color={managementPlanForm.plan ? "primary" : "dark"}
                    disabled={!managementPlanForm.plan || managementPlanSaving}
                    onClick={handleEditSelectedManagementPlan}
                    sx={{
                      height: "calc(40px + 0.3mm)",
                      minWidth: 96,
                      color: managementPlanForm.plan ? "#fff" : "text.secondary",
                      "&.Mui-disabled": {
                        color: "text.secondary",
                        WebkitTextFillColor: "currentColor",
                      },
                    }}
                  >
                    Edit
                  </Button>
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDBox display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                  <Button
                    variant="text"
                    color="dark"
                    onClick={handleCloseManagementPlanDialog}
                    disabled={managementPlanSaving}
                  >
                    Cancel
                  </Button>
                </MDBox>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
      {viewManagementPlan && (
        <ManagementPlanViewDialog
          key={viewManagementPlan.id}
          plan={viewManagementPlan}
          onClose={() => setViewManagementPlan(null)}
        />
      )}
      <Dialog
        open={managementPlanStrainDialogOpen}
        onClose={handleCloseManagementPlanStrainDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox component="span">Edit Plan Parameters</MDBox>
            <IconButton
              aria-label="close plan parameters"
              size="small"
              onClick={handleCloseManagementPlanStrainDialog}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Facility Conditions
              </Typography>
            </Grid>
            {[
              ["day", "Day", "select", managementPlanStrainFieldOptions.days, 6],
              ["category", "Category", "select", managementPlanStrainFieldOptions.categories, 6],
              ["parametre", "Parametre", "select", managementPlanStrainFieldOptions.parametres, 6],
              ["equality", "Equality", "select", managementPlanStrainFieldOptions.equalities, 6],
              ["lowValue", "Low Value", "number", [], 4],
              ["highValue", "High Value", "number", [], 4],
              ["enable", "Enable", "select", managementPlanStrainFieldOptions.enable, 4],
            ].map(([key, label, fieldType, options, md]) => (
              <Grid item xs={12} md={md} key={key}>
                <TextField
                  select={fieldType === "select"}
                  label={label}
                  fullWidth
                  size="medium"
                  type={fieldType === "number" ? "number" : undefined}
                  value={
                    fieldType === "time"
                      ? getScheduledTimeInputValue(managementPlanStrainForm[key])
                      : managementPlanStrainForm[key]
                  }
                  onChange={(event) =>
                    handleManagementPlanStrainFieldChange(key, event.target.value)
                  }
                  disabled={
                    managementPlanStrainFieldDisabled[key] ??
                    (managementPlanStrainLoading || managementPlanStrainSaving)
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 40,
                    },
                    "& .MuiSelect-select": {
                      minHeight: "1.4375em !important",
                      display: "flex",
                      alignItems: "center",
                      py: "8.5px",
                    },
                  }}
                >
                  {fieldType === "select" && managementPlanStrainLoading ? (
                    <MenuItem value="" disabled>
                      Loading...
                    </MenuItem>
                  ) : fieldType === "select" && options.length === 0 ? (
                    <MenuItem value="" disabled>
                      No options
                    </MenuItem>
                  ) : fieldType === "select" ? (
                    [
                      <MenuItem key="empty" value="" disabled />,
                      ...options.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      )),
                    ]
                  ) : null}
                </TextField>
              </Grid>
            ))}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <MDBox display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Scheduled Activities
                </Typography>
                <IconButton
                  aria-label="add scheduled activity"
                  size="small"
                  onClick={handleAddScheduledActivity}
                  disabled={
                    managementPlanStrainLoading ||
                    managementPlanStrainSaving ||
                    !canAddScheduledActivity
                  }
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: "grey.100",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.800",
                    "&:hover": { bgcolor: "grey.200" },
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </MDBox>
            </Grid>
            {[
              ["activityCategory", "Category", "select", managementPlanActivityTypeOptions, 6],
              ["activityName", "Name", "select", managementPlanActivityNameOptions, 6],
              ["startBy", "Start Time", "time", [], 4],
              ["endBy", "End By", "time", [], 4],
              ["dosage", "Dosage", "number", [], 4],
            ].map(([key, label, fieldType, options, md]) => (
              <Grid item xs={12} md={md} key={key}>
                <TextField
                  select={fieldType === "select"}
                  label={label}
                  fullWidth
                  size="medium"
                  type={
                    fieldType === "time" ? "time" : fieldType === "number" ? "number" : undefined
                  }
                  InputLabelProps={fieldType === "time" ? { shrink: true } : undefined}
                  inputProps={fieldType === "number" ? { min: 0, step: 1 } : undefined}
                  value={
                    fieldType === "time"
                      ? getScheduledTimeInputValue(managementPlanStrainForm[key])
                      : managementPlanStrainForm[key]
                  }
                  onChange={(event) =>
                    handleManagementPlanStrainFieldChange(key, event.target.value)
                  }
                  disabled={
                    managementPlanStrainLoading ||
                    managementPlanStrainSaving ||
                    (key === "activityName" && managementPlanActivityNameLoading)
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 40,
                    },
                    "& .MuiSelect-select": {
                      minHeight: "1.4375em !important",
                      display: "flex",
                      alignItems: "center",
                      py: "8.5px",
                    },
                  }}
                >
                  {fieldType === "select" &&
                  (managementPlanStrainLoading ||
                    (key === "activityName" && managementPlanActivityNameLoading)) ? (
                    <MenuItem value="" disabled>
                      Loading...
                    </MenuItem>
                  ) : fieldType === "select" && options.length === 0 ? (
                    <MenuItem value="" disabled>
                      No options
                    </MenuItem>
                  ) : fieldType === "select" ? (
                    [
                      <MenuItem key="empty" value="" disabled />,
                      ...options.map((option) => (
                        <MenuItem
                          key={typeof option === "string" ? option : option.id}
                          value={typeof option === "string" ? option : option.name}
                        >
                          {typeof option === "string" ? option : option.name}
                        </MenuItem>
                      )),
                    ]
                  ) : null}
                </TextField>
              </Grid>
            ))}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Button
                    variant="outlined"
                    color="dark"
                    fullWidth
                    disabled={managementPlanStrainLoading || managementPlanStrainSaving}
                    onClick={handlePreviewManagementPlanStrain}
                    sx={{ height: 40 }}
                  >
                    Preview
                  </Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Plan Name"
                    fullWidth
                    size="medium"
                    value={managementPlanStrainForm.planName}
                    onChange={(event) =>
                      handleManagementPlanStrainFieldChange("planName", event.target.value)
                    }
                    disabled={managementPlanStrainLoading || managementPlanStrainSaving}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: 40,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDBox display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveManagementPlanStrain}
                      disabled={
                        managementPlanStrainLoading ||
                        managementPlanStrainSaving ||
                        !managementPlanStrainForm.planName.trim()
                      }
                      sx={{
                        bgcolor: "#1f4e79",
                        color: "#fff",
                        height: 40,
                        minWidth: 96,
                        "&:hover": { bgcolor: "#173b5c" },
                      }}
                    >
                      Save
                    </Button>
                  </MDBox>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(scheduledActivityConfirmation)}
        autoHideDuration={2500}
        onClose={() => setScheduledActivityConfirmation("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          top: "50% !important",
          transform: "translateY(-50%)",
        }}
      >
        <Alert
          onClose={() => setScheduledActivityConfirmation("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {scheduledActivityConfirmation}
        </Alert>
      </Snackbar>
      <Dialog
        open={Boolean(managementPlanSaveConfirmation)}
        onClose={() => setManagementPlanSaveConfirmation("")}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox component="span">Saved</MDBox>
            <IconButton
              aria-label="close save confirmation"
              size="small"
              onClick={() => setManagementPlanSaveConfirmation("")}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">{managementPlanSaveConfirmation}</Typography>
          <MDBox display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setManagementPlanSaveConfirmation("")}
              sx={{ color: "#fff", minWidth: 96 }}
            >
              OK
            </Button>
          </MDBox>
        </DialogContent>
      </Dialog>
      <Dialog
        open={managementPlanPreviewOpen}
        onClose={() => setManagementPlanPreviewOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox component="span">Preview Plan Parameters</MDBox>
            <MDBox display="flex" alignItems="center" gap={1}>
              {[
                ["facility", "Facility Conditions"],
                ["scheduled", "Scheduled Activities"],
              ].map(([page, label]) => (
                <Tooltip key={page} title={label}>
                  <IconButton
                    aria-label={label}
                    size="small"
                    onClick={() => setManagementPlanPreviewPage(page)}
                    sx={{
                      width: 22,
                      height: 22,
                      p: 0,
                      "&::before": {
                        content: '""',
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        bgcolor: managementPlanPreviewPage === page ? "primary.main" : "grey.400",
                        display: "block",
                      },
                    }}
                  />
                </Tooltip>
              ))}
              <IconButton
                aria-label="close plan preview"
                size="small"
                onClick={() => setManagementPlanPreviewOpen(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </MDBox>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: "65vh",
              overflow: "auto",
              borderRadius: 0,
              boxShadow: "none",
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: managementPlanPreviewPage === "facility" ? "15cm" : "13cm",
                borderCollapse: "collapse",
                ...(managementPlanPreviewPage === "scheduled"
                  ? {
                      "& th:nth-of-type(2), & td:nth-of-type(2)": {
                        width: "5cm",
                      },
                    }
                  : {}),
                "& th, & td": {
                  border: "1px solid",
                  borderColor: "grey.300",
                  fontSize: "0.8rem",
                  lineHeight: 1.2,
                  p: 0.75,
                  textAlign: "center",
                },
              }}
            >
              <TableBody>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  {managementPlanPreviewPage === "facility" ? (
                    <>
                      <TableCell sx={{ fontWeight: 700 }} />
                      <TableCell colSpan={5} sx={{ fontWeight: 700 }}>
                        Facility Conditions
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ fontWeight: 700 }} />
                      <TableCell colSpan={4} sx={{ fontWeight: 700 }}>
                        Scheduled Activities
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} />
                    </>
                  )}
                </TableRow>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  {(managementPlanPreviewPage === "facility"
                    ? ["Day", "Parametre", "Equality", "Low Value", "High Value", "Enable"]
                    : ["Day", "Parametre", "Start Time", "Complete By", "Dosage", ""]
                  ).map((heading) => (
                    <TableCell key={heading} sx={{ fontWeight: 700 }}>
                      {heading}
                    </TableCell>
                  ))}
                </TableRow>
                {managementPlanPreviewRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={managementPlanPreviewPage === "facility" ? 6 : 6}
                      sx={{ py: 2, color: "text.secondary" }}
                    >
                      No preview data
                    </TableCell>
                  </TableRow>
                ) : (
                  managementPlanPreviewRows.map((row) => (
                    <TableRow key={row.id}>
                      {managementPlanPreviewPage === "facility" ? (
                        <>
                          <TableCell>{row.day}</TableCell>
                          <TableCell>{row.parametre}</TableCell>
                          <TableCell>{row.equality}</TableCell>
                          <TableCell>{row.lowValue}</TableCell>
                          <TableCell>{row.highValue}</TableCell>
                          <TableCell>{row.enable}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{row.day}</TableCell>
                          <TableCell>{row.parametre}</TableCell>
                          <TableCell>{getScheduledTimeInputValue(row.startTime)}</TableCell>
                          <TableCell>{getScheduledTimeInputValue(row.endBy)}</TableCell>
                          <TableCell>{row.dosage}</TableCell>
                          <TableCell>
                            <DeleteIcon
                              onClick={() => handleDeleteScheduledActivityPreviewRow(row)}
                              sx={{ fontSize: 24, color: "text.secondary", cursor: "pointer" }}
                            />
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
      <Dialog
        open={docsDialogOpen}
        onClose={() => setDocsDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox component="span" sx={{ fontWeight: 600 }}>
              DOCs
            </MDBox>
            <IconButton
              aria-label="close DOCs"
              size="small"
              onClick={() => setDocsDialogOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <SectionTitle>Supplier Details</SectionTitle>
          <Paper variant="outlined" sx={{ pb: "0.3cm", borderRadius: 0 }}>
            <MDBox
              sx={{
                mt: "0.3cm",
                ml: "0.5cm",
                width: "calc(100% - 0.5cm)",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "0.2cm",
                overflow: "hidden",
              }}
            >
              <TableContainer component={MDBox} sx={{ boxShadow: "none" }}>
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "17.28cm",
                    minWidth: "17.28cm",
                    borderCollapse: "collapse",
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "grey.300",
                      fontSize: "0.8rem",
                      lineHeight: 1.2,
                      p: 0.75,
                      textAlign: "center",
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "6.48cm" }} />
                    <col style={{ width: "7.92cm" }} />
                    <col style={{ width: "2.88cm" }} />
                  </colgroup>
                  <TableBody>
                    <TableRow sx={{ height: "1.5cm", bgcolor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active
                          direction={
                            supplierSort.orderBy === "supplierCode" ? supplierSort.order : "asc"
                          }
                          onClick={() => handleSupplierSort("supplierCode")}
                          hideSortIcon={false}
                          sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                        >
                          Supplier Code
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active
                          direction={
                            supplierSort.orderBy === "supplierName" ? supplierSort.order : "asc"
                          }
                          onClick={() => handleSupplierSort("supplierName")}
                          hideSortIcon={false}
                          sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                        >
                          Supplier Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <IconButton
                          size="small"
                          onClick={handleOpenSupplierDialog}
                          aria-label="add supplier"
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.800",
                            "&:hover": { bgcolor: "grey.200" },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {supplierLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          Loading suppliers...
                        </TableCell>
                      </TableRow>
                    ) : supplierRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          No data
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedSupplierRows.map((row) => (
                        <TableRow key={row.id} sx={{ "& td": { fontSize: "0.85rem" } }}>
                          <TableCell>{row.supplierCode}</TableCell>
                          <TableCell>{row.supplierName}</TableCell>
                          <TableCell>
                            <MDBox
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                justifyContent: "center",
                                width: "100%",
                              }}
                            >
                              <EditIcon
                                onClick={() => handleOpenSupplierEditDialog(row)}
                                sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                              />
                              <DeleteIcon
                                onClick={() => handleDeleteSupplierRow(row)}
                                sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                              />
                            </MDBox>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          </Paper>

          <SectionTitle>Breeder Details</SectionTitle>
          <Paper variant="outlined" sx={{ pb: "0.3cm", borderRadius: 0 }}>
            <MDBox
              sx={{
                mt: "0.3cm",
                ml: "0.5cm",
                width: "calc(100% - 0.5cm)",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "0.2cm",
                overflow: "hidden",
              }}
            >
              <TableContainer component={MDBox} sx={{ boxShadow: "none" }}>
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "17.28cm",
                    minWidth: "17.28cm",
                    borderCollapse: "collapse",
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "grey.300",
                      fontSize: "0.8rem",
                      lineHeight: 1.2,
                      p: 0.75,
                      textAlign: "center",
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "6.48cm" }} />
                    <col style={{ width: "7.92cm" }} />
                    <col style={{ width: "2.88cm" }} />
                  </colgroup>
                  <TableBody>
                    <TableRow sx={{ height: "1.5cm", bgcolor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active
                          direction={
                            breederSort.orderBy === "breederCode" ? breederSort.order : "asc"
                          }
                          onClick={() => handleBreederSort("breederCode")}
                          hideSortIcon={false}
                          sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                        >
                          Breeder Code
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active
                          direction={
                            breederSort.orderBy === "description" ? breederSort.order : "asc"
                          }
                          onClick={() => handleBreederSort("description")}
                          hideSortIcon={false}
                          sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                        >
                          Description
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <IconButton
                          size="small"
                          onClick={handleOpenBreederDialog}
                          aria-label="add breeder"
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.800",
                            "&:hover": { bgcolor: "grey.200" },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {breederLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          Loading breeders...
                        </TableCell>
                      </TableRow>
                    ) : breederRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          No data
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedBreederRows.map((row) => (
                        <TableRow key={row.id} sx={{ "& td": { fontSize: "0.85rem" } }}>
                          <TableCell>{row.breederCode}</TableCell>
                          <TableCell>{row.description}</TableCell>
                          <TableCell>
                            <MDBox
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                justifyContent: "center",
                                width: "100%",
                              }}
                            >
                              <EditIcon
                                onClick={() => handleOpenBreederEditDialog(row)}
                                sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                              />
                              <DeleteIcon
                                onClick={() => handleDeleteBreederRow(row)}
                                sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                              />
                            </MDBox>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          </Paper>
        </DialogContent>
      </Dialog>
      <Dialog open={supplierDialogOpen} onClose={handleCloseSupplierDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1.5 }}>
          {supplierEditingId == null ? "Add Supplier" : "Edit Supplier"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Supplier Code"
                fullWidth
                size="small"
                value={supplierForm.supplierCode}
                onChange={(event) =>
                  setSupplierForm((form) => ({ ...form, supplierCode: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Supplier Name"
                fullWidth
                size="small"
                value={supplierForm.supplierName}
                onChange={(event) =>
                  setSupplierForm((form) => ({ ...form, supplierName: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="flex-end" gap={1}>
                <Button variant="text" color="dark" onClick={handleCloseSupplierDialog}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveSupplierRow}
                  disabled={supplierSaving}
                  sx={{ color: "#fff" }}
                >
                  {supplierEditingId == null ? "Add" : "Save"}
                </Button>
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog open={breederDialogOpen} onClose={handleCloseBreederDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1.5 }}>
          {breederEditingId == null ? "Add Breeder" : "Edit Breeder"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Breeder Code"
                fullWidth
                size="small"
                value={breederForm.breederCode}
                onChange={(event) =>
                  setBreederForm((form) => ({ ...form, breederCode: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Description"
                fullWidth
                size="small"
                value={breederForm.description}
                onChange={(event) =>
                  setBreederForm((form) => ({ ...form, description: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="flex-end" gap={1}>
                <Button variant="text" color="dark" onClick={handleCloseBreederDialog}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveBreederRow}
                  disabled={breederSaving}
                  sx={{ color: "#fff" }}
                >
                  {breederEditingId == null ? "Add" : "Save"}
                </Button>
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog
        open={feedDialogOpen}
        onClose={() => setFeedDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox component="span" sx={{ fontWeight: 600 }}>
              Feed
            </MDBox>
            <IconButton
              aria-label="close Feed"
              size="small"
              onClick={() => setFeedDialogOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <SectionTitle>Feed Stage</SectionTitle>
          <Paper variant="outlined" sx={{ pb: "0.3cm", borderRadius: 0 }}>
            <MDBox
              sx={{
                mt: "0.3cm",
                ml: "0.5cm",
                width: "calc(100% - 0.5cm)",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "0.2cm",
                overflow: "hidden",
              }}
            >
              <TableContainer component={MDBox} sx={{ boxShadow: "none" }}>
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "17.28cm",
                    minWidth: "17.28cm",
                    borderCollapse: "collapse",
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "grey.300",
                      fontSize: "0.8rem",
                      lineHeight: 1.2,
                      p: 0.75,
                      textAlign: "center",
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "14.4cm" }} />
                    <col style={{ width: "2.88cm" }} />
                  </colgroup>
                  <TableBody>
                    <TableRow sx={{ height: "1.5cm", bgcolor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active
                          direction={feedStageSort.order}
                          onClick={handleFeedStageSort}
                          hideSortIcon={false}
                          sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                        >
                          Stage
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <IconButton
                          size="small"
                          onClick={handleOpenFeedStageDialog}
                          aria-label="add feed stage"
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.800",
                            "&:hover": { bgcolor: "grey.200" },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {feedStageLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          Loading feed stages...
                        </TableCell>
                      </TableRow>
                    ) : feedStageRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          No data
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedFeedStageRows.map((row) => (
                        <TableRow key={row.id} sx={{ "& td": { fontSize: "0.85rem" } }}>
                          <TableCell>{row.stage}</TableCell>
                          <TableCell>
                            <MDBox
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                justifyContent: "center",
                                width: "100%",
                              }}
                            >
                              <EditIcon
                                onClick={() => handleOpenFeedStageEditDialog(row)}
                                sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                              />
                              <DeleteIcon
                                onClick={() => handleDeleteFeedStageRow(row)}
                                sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                              />
                            </MDBox>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          </Paper>

          <SectionTitle>Feed Name</SectionTitle>
          <Paper variant="outlined" sx={{ pb: "0.3cm", borderRadius: 0 }}>
            <MDBox
              sx={{
                mt: "0.3cm",
                ml: "0.5cm",
                width: "calc(100% - 0.5cm)",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "0.2cm",
                overflow: "hidden",
              }}
            >
              <TableContainer component={MDBox} sx={{ boxShadow: "none" }}>
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "17.28cm",
                    minWidth: "17.28cm",
                    borderCollapse: "collapse",
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "grey.300",
                      fontSize: "0.8rem",
                      lineHeight: 1.2,
                      p: 0.75,
                      textAlign: "center",
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "6.48cm" }} />
                    <col style={{ width: "7.92cm" }} />
                    <col style={{ width: "2.88cm" }} />
                  </colgroup>
                  <TableBody>
                    <TableRow sx={{ height: "1.5cm", bgcolor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active
                          direction={feedNameSort.orderBy === "name" ? feedNameSort.order : "asc"}
                          onClick={() => handleFeedNameSort("name")}
                          hideSortIcon={false}
                          sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active
                          direction={
                            feedNameSort.orderBy === "description" ? feedNameSort.order : "asc"
                          }
                          onClick={() => handleFeedNameSort("description")}
                          hideSortIcon={false}
                          sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                        >
                          Description
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <IconButton
                          size="small"
                          onClick={handleOpenFeedNameDialog}
                          aria-label="add feed name"
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.800",
                            "&:hover": { bgcolor: "grey.200" },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {feedNameLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          Loading feed names...
                        </TableCell>
                      </TableRow>
                    ) : feedNameRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          No data
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedFeedNameRows.map((row) => (
                        <TableRow key={row.id} sx={{ "& td": { fontSize: "0.85rem" } }}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.description}</TableCell>
                          <TableCell>
                            <MDBox
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                justifyContent: "center",
                                width: "100%",
                              }}
                            >
                              <EditIcon
                                onClick={() => handleOpenFeedNameEditDialog(row)}
                                sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                              />
                              <DeleteIcon
                                onClick={() => handleDeleteFeedNameRow(row)}
                                sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                              />
                            </MDBox>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          </Paper>
          <SectionTitle>Supplier</SectionTitle>
          <Paper variant="outlined" sx={{ pb: "0.3cm", borderRadius: 0 }}>
            <MDBox
              sx={{
                mt: "0.3cm",
                ml: "0.5cm",
                width: "calc(100% - 0.5cm)",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "0.2cm",
                overflow: "hidden",
              }}
            >
              <TableContainer component={MDBox} sx={{ boxShadow: "none" }}>
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "17.28cm",
                    minWidth: "17.28cm",
                    borderCollapse: "collapse",
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "grey.300",
                      fontSize: "0.8rem",
                      lineHeight: 1.2,
                      p: 0.75,
                      textAlign: "center",
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "6.48cm" }} />
                    <col style={{ width: "7.92cm" }} />
                    <col style={{ width: "2.88cm" }} />
                  </colgroup>
                  <TableBody>
                    <TableRow sx={{ height: "1.5cm", bgcolor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active
                          direction={
                            feedSupplierSort.orderBy === "name" ? feedSupplierSort.order : "asc"
                          }
                          onClick={() => handleFeedSupplierSort("name")}
                          hideSortIcon={false}
                          sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active
                          direction={
                            feedSupplierSort.orderBy === "description"
                              ? feedSupplierSort.order
                              : "asc"
                          }
                          onClick={() => handleFeedSupplierSort("description")}
                          hideSortIcon={false}
                          sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                        >
                          Description
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <IconButton
                          size="small"
                          onClick={handleOpenFeedSupplierDialog}
                          aria-label="add feed supplier"
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.800",
                            "&:hover": { bgcolor: "grey.200" },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {feedSupplierLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          Loading feed suppliers...
                        </TableCell>
                      </TableRow>
                    ) : feedSupplierRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 2, color: "text.secondary" }}
                        >
                          No data
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedFeedSupplierRows.map((row) => (
                        <TableRow key={row.id} sx={{ "& td": { fontSize: "0.85rem" } }}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.description}</TableCell>
                          <TableCell>
                            <MDBox
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                justifyContent: "center",
                                width: "100%",
                              }}
                            >
                              <EditIcon
                                onClick={() => handleOpenFeedSupplierEditDialog(row)}
                                sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                              />
                              <DeleteIcon
                                onClick={() => handleDeleteFeedSupplierRow(row)}
                                sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                              />
                            </MDBox>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          </Paper>
        </DialogContent>
      </Dialog>
      <Dialog
        open={feedStageDialogOpen}
        onClose={handleCloseFeedStageDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          {feedStageEditingId == null ? "Add Feed Stage" : "Edit Feed Stage"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12}>
              <TextField
                label="Stage"
                fullWidth
                size="small"
                value={feedStageForm.stage}
                onChange={(event) =>
                  setFeedStageForm((form) => ({ ...form, stage: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="flex-end" gap={1}>
                <Button variant="text" color="dark" onClick={handleCloseFeedStageDialog}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveFeedStageRow}
                  disabled={feedStageSaving}
                  sx={{ color: "#fff" }}
                >
                  {feedStageEditingId == null ? "Add" : "Save"}
                </Button>
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog open={feedNameDialogOpen} onClose={handleCloseFeedNameDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1.5 }}>
          {feedNameEditingId == null ? "Add Feed Name" : "Edit Feed Name"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                fullWidth
                size="small"
                value={feedNameForm.name}
                onChange={(event) =>
                  setFeedNameForm((form) => ({ ...form, name: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Description"
                fullWidth
                size="small"
                value={feedNameForm.description}
                onChange={(event) =>
                  setFeedNameForm((form) => ({ ...form, description: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="flex-end" gap={1}>
                <Button variant="text" color="dark" onClick={handleCloseFeedNameDialog}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveFeedNameRow}
                  disabled={feedNameSaving}
                  sx={{ color: "#fff" }}
                >
                  {feedNameEditingId == null ? "Add" : "Save"}
                </Button>
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog
        open={feedSupplierDialogOpen}
        onClose={handleCloseFeedSupplierDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          {feedSupplierEditingId == null ? "Add Supplier" : "Edit Supplier"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                fullWidth
                size="small"
                value={feedSupplierForm.name}
                onChange={(event) =>
                  setFeedSupplierForm((form) => ({ ...form, name: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Description"
                fullWidth
                size="small"
                value={feedSupplierForm.description}
                onChange={(event) =>
                  setFeedSupplierForm((form) => ({ ...form, description: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="flex-end" gap={1}>
                <Button variant="text" color="dark" onClick={handleCloseFeedSupplierDialog}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveFeedSupplierRow}
                  disabled={feedSupplierSaving}
                  sx={{ color: "#fff" }}
                >
                  {feedSupplierEditingId == null ? "Add" : "Save"}
                </Button>
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog
        open={healthDialogOpen}
        onClose={() => setHealthDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox component="span" sx={{ fontWeight: 600 }}>
              Health
            </MDBox>
            <IconButton
              aria-label="close Health"
              size="small"
              onClick={() => setHealthDialogOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {healthSections.map((section) => {
            const sort = healthSort[section] ?? { orderBy: "name", order: "asc" };
            const rows = healthRows[section] ?? [];
            const sortedRows = getSortedHealthRows(section);
            const isLoading = healthLoading[section];

            return (
              <MDBox key={section}>
                <SectionTitle>{section}</SectionTitle>
                <Paper variant="outlined" sx={{ pb: "0.3cm", borderRadius: 0 }}>
                  <MDBox
                    sx={{
                      mt: "0.3cm",
                      ml: "0.5cm",
                      width: "calc(100% - 0.5cm)",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      gap: "0.2cm",
                      overflow: "hidden",
                    }}
                  >
                    <TableContainer component={MDBox} sx={{ boxShadow: "none" }}>
                      <Table
                        size="small"
                        sx={{
                          tableLayout: "fixed",
                          width: "17.28cm",
                          minWidth: "17.28cm",
                          borderCollapse: "collapse",
                          "& th, & td": {
                            border: "1px solid",
                            borderColor: "grey.300",
                            fontSize: "0.8rem",
                            lineHeight: 1.2,
                            p: 0.75,
                            textAlign: "center",
                          },
                        }}
                      >
                        <colgroup>
                          <col style={{ width: "6.48cm" }} />
                          <col style={{ width: "7.92cm" }} />
                          <col style={{ width: "2.88cm" }} />
                        </colgroup>
                        <TableBody>
                          <TableRow sx={{ height: "1.5cm", bgcolor: "grey.100" }}>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active
                                direction={sort.orderBy === "name" ? sort.order : "asc"}
                                onClick={() => handleHealthSort(section, "name")}
                                hideSortIcon={false}
                                sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                              >
                                Name
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active
                                direction={sort.orderBy === "description" ? sort.order : "asc"}
                                onClick={() => handleHealthSort(section, "description")}
                                hideSortIcon={false}
                                sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                              >
                                Description
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenHealthItemDialog(section)}
                                aria-label={`add ${section}`}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  bgcolor: "grey.100",
                                  borderRadius: 1,
                                  border: "1px solid",
                                  borderColor: "grey.800",
                                  "&:hover": { bgcolor: "grey.200" },
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          {isLoading ? (
                            <TableRow>
                              <TableCell
                                colSpan={3}
                                align="center"
                                sx={{ py: 2, color: "text.secondary" }}
                              >
                                Loading {section.toLowerCase()}...
                              </TableCell>
                            </TableRow>
                          ) : rows.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={3}
                                align="center"
                                sx={{ py: 2, color: "text.secondary" }}
                              >
                                No data
                              </TableCell>
                            </TableRow>
                          ) : (
                            sortedRows.map((row) => (
                              <TableRow key={row.id} sx={{ "& td": { fontSize: "0.85rem" } }}>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{row.description}</TableCell>
                                <TableCell>
                                  <MDBox
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 1,
                                      justifyContent: "center",
                                      width: "100%",
                                    }}
                                  >
                                    <EditIcon
                                      onClick={() => handleOpenHealthItemEditDialog(section, row)}
                                      sx={{
                                        fontSize: 28,
                                        color: "text.secondary",
                                        cursor: "pointer",
                                      }}
                                    />
                                    <DeleteIcon
                                      onClick={() => handleDeleteHealthItemRow(section, row)}
                                      sx={{
                                        fontSize: 28,
                                        color: "text.secondary",
                                        cursor: "pointer",
                                      }}
                                    />
                                  </MDBox>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </MDBox>
                </Paper>
              </MDBox>
            );
          })}
        </DialogContent>
      </Dialog>
      <Dialog
        open={healthItemDialogOpen}
        onClose={handleCloseHealthItemDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          {healthItemEditingId == null ? `Add ${healthItemSection}` : `Edit ${healthItemSection}`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                fullWidth
                size="small"
                value={healthItemForm.name}
                onChange={(event) =>
                  setHealthItemForm((form) => ({ ...form, name: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Description"
                fullWidth
                size="small"
                value={healthItemForm.description}
                onChange={(event) =>
                  setHealthItemForm((form) => ({ ...form, description: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="flex-end" gap={1}>
                <Button variant="text" color="dark" onClick={handleCloseHealthItemDialog}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveHealthItemRow}
                  disabled={healthSaving}
                  sx={{ color: "#fff" }}
                >
                  {healthItemEditingId == null ? "Add" : "Save"}
                </Button>
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog
        open={systemsDialogOpen}
        onClose={() => setSystemsDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox component="span" sx={{ fontWeight: 600 }}>
              Systems
            </MDBox>
            <IconButton
              aria-label="close Systems"
              size="small"
              onClick={() => setSystemsDialogOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {systemSections.map((section) => {
            const sort = systemSort[section] ?? { orderBy: "makeModel", order: "asc" };
            const rows = systemRows[section] ?? [];
            const sortedRows = getSortedSystemRows(section);
            const isLoading = systemLoading[section];

            return (
              <MDBox key={section}>
                <SectionTitle>{section}</SectionTitle>
                <Paper variant="outlined" sx={{ pb: "0.3cm", borderRadius: 0 }}>
                  <MDBox
                    sx={{
                      mt: "0.3cm",
                      ml: "0.5cm",
                      width: "calc(100% - 0.5cm)",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      gap: "0.2cm",
                      overflow: "hidden",
                    }}
                  >
                    <TableContainer component={MDBox} sx={{ boxShadow: "none" }}>
                      <Table
                        size="small"
                        sx={{
                          tableLayout: "fixed",
                          width: "17.28cm",
                          minWidth: "17.28cm",
                          borderCollapse: "collapse",
                          "& th, & td": {
                            border: "1px solid",
                            borderColor: "grey.300",
                            fontSize: "0.8rem",
                            lineHeight: 1.2,
                            p: 0.75,
                            textAlign: "center",
                          },
                        }}
                      >
                        <colgroup>
                          <col style={{ width: "6.48cm" }} />
                          <col style={{ width: "7.92cm" }} />
                          <col style={{ width: "2.88cm" }} />
                        </colgroup>
                        <TableBody>
                          <TableRow sx={{ height: "1.5cm", bgcolor: "grey.100" }}>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active
                                direction={sort.orderBy === "makeModel" ? sort.order : "asc"}
                                onClick={() => handleSystemSort(section, "makeModel")}
                                hideSortIcon={false}
                                sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                              >
                                Make/Model
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active
                                direction={sort.orderBy === "description" ? sort.order : "asc"}
                                onClick={() => handleSystemSort(section, "description")}
                                hideSortIcon={false}
                                sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                              >
                                Description
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenSystemItemDialog(section)}
                                aria-label={`add ${section}`}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  bgcolor: "grey.100",
                                  borderRadius: 1,
                                  border: "1px solid",
                                  borderColor: "grey.800",
                                  "&:hover": { bgcolor: "grey.200" },
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          {isLoading ? (
                            <TableRow>
                              <TableCell
                                colSpan={3}
                                align="center"
                                sx={{ py: 2, color: "text.secondary" }}
                              >
                                Loading {section.toLowerCase()}...
                              </TableCell>
                            </TableRow>
                          ) : rows.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={3}
                                align="center"
                                sx={{ py: 2, color: "text.secondary" }}
                              >
                                No data
                              </TableCell>
                            </TableRow>
                          ) : (
                            sortedRows.map((row) => (
                              <TableRow key={row.id} sx={{ "& td": { fontSize: "0.85rem" } }}>
                                <TableCell>{row.makeModel}</TableCell>
                                <TableCell>{row.description}</TableCell>
                                <TableCell>
                                  <MDBox
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 1,
                                      justifyContent: "center",
                                      width: "100%",
                                    }}
                                  >
                                    <EditIcon
                                      onClick={() => handleOpenSystemItemEditDialog(section, row)}
                                      sx={{
                                        fontSize: 28,
                                        color: "text.secondary",
                                        cursor: "pointer",
                                      }}
                                    />
                                    <DeleteIcon
                                      onClick={() => handleDeleteSystemItemRow(section, row)}
                                      sx={{
                                        fontSize: 28,
                                        color: "text.secondary",
                                        cursor: "pointer",
                                      }}
                                    />
                                  </MDBox>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </MDBox>
                </Paper>
              </MDBox>
            );
          })}
        </DialogContent>
      </Dialog>
      <Dialog
        open={systemItemDialogOpen}
        onClose={handleCloseSystemItemDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          {systemItemEditingId == null ? `Add ${systemItemSection}` : `Edit ${systemItemSection}`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Make/Model"
                fullWidth
                size="small"
                value={systemItemForm.makeModel}
                onChange={(event) =>
                  setSystemItemForm((form) => ({ ...form, makeModel: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Description"
                fullWidth
                size="small"
                value={systemItemForm.description}
                onChange={(event) =>
                  setSystemItemForm((form) => ({ ...form, description: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="flex-end" gap={1}>
                <Button variant="text" color="dark" onClick={handleCloseSystemItemDialog}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveSystemItemRow}
                  disabled={systemSaving}
                  sx={{ color: "#fff" }}
                >
                  {systemItemEditingId == null ? "Add" : "Save"}
                </Button>
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default AdministrationFacilities;
